import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { translations } from "./translations";

// Extracted pages (modular migration phase 2)
import {
  MarketplaceRulesPage,
  PrivacyPolicyPage,
  TermsOfSalePage,
  TermsOfUsePage,
  AboutPage,
  UnsubscribePage,
} from "./pages";
import { SEO, LocalBusinessSchema, ServiceSchema, ProductSchema, BreadcrumbSchema } from "./components/SEO";
import {
  Menu,
  X,
  Wrench,
  Home,
  Truck,
  Car,
  Sparkles,
  Package,
  Plane,
  Settings,
  CheckCircle,
  Users,
  Clock,
  Shield,
  Star,
  MapPin,
  ChevronDown,
  Send,
  ArrowRight,
  LogOut,
  MessageSquare,
  Plus,
  Search,
  User,
  ArrowLeft,
  Loader2,
  Zap,
  Globe,
  Camera,
  Trash2,
  Mail,
  ExternalLink,
  Lock,
  Eye,
  EyeOff,
  Heart,
  LayoutDashboard,
  FileText,
  Newspaper,
  Edit,
  Pencil,
  Bell,
  PlusCircle,
} from "lucide-react";
import { searchCities, formatCity } from "./luxembourgCities";

// Use relative URL for production (when BACKEND_URL is not set or same domain)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Price formatting helper - skips € for free items
const formatPrice = (price) => {
  if (!price) return "";
  const priceStr = String(price).trim().toLowerCase();
  const isFree = ["gratuit", "free", "gratis", "0"].includes(priceStr);
  if (isFree) return price;
  return String(price).includes("€") ? price : `${price} €`;
};

// User profile URL helper - uses slug if available, falls back to user_id
const getUserProfileUrl = (user) => {
  if (!user) return "/";
  const identifier = user.slug || user.user_id || user;
  return `/user/${identifier}`;
};

// Safe image component that handles broken images
const SafeImage = ({ src, alt, className, fallback = null, loading: loadingAttr = "lazy", fetchpriority = "auto" }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError || !src) {
    return fallback || (
      <div className={`${className} bg-slate-100 flex items-center justify-center`}>
        <span className="text-slate-400 text-sm">📷</span>
      </div>
    );
  }
  
  // Render the <img> directly (no skeleton placeholder hiding it).
  // - The native browser fade-in is faster than a JS state toggle.
  // - bg-slate-100 acts as a CSS placeholder while the img is loading.
  // - loading="eager" + fetchpriority="high" make above-the-fold images appear immediately on mobile.
  return (
    <img 
      src={src}
      alt={alt || ""}
      loading={loadingAttr}
      decoding="async"
      fetchpriority={fetchpriority}
      className={`${className} bg-slate-100`}
      onError={() => setHasError(true)}
    />
  );
};

// ==================== IMAGE LIGHTBOX COMPONENT ====================
const ImageLightbox = ({ images, initialIndex = 0, onClose, getImageUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      if (e.key === "ArrowRight") setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [images.length, onClose]);

  const imageUrl = getImageUrl ? getImageUrl(images[currentIndex]) : images[currentIndex];

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-slate-300 z-10"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>
      
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-4 text-white hover:text-slate-300 bg-black/40 rounded-full p-2"
            data-testid="lightbox-prev"
          >
            <ArrowLeft className="w-8 h-8" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 text-white hover:text-slate-300 bg-black/40 rounded-full p-2"
            data-testid="lightbox-next"
          >
            <ArrowRight className="w-8 h-8" />
          </button>
        </>
      )}
      
      <img
        src={imageUrl}
        alt={`Image ${currentIndex + 1}`}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? "bg-white w-4" : "bg-white/50"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Configure axios
axios.defaults.withCredentials = true;

// Authentication is handled via httpOnly cookies - no localStorage token needed

// Global helper to resolve user/post/product picture URLs (storage path → full URL)
const resolvePictureUrl = (picture) => {
  if (!picture) return null;
  if (picture.startsWith("letzservices/")) return `${API}/files/${picture}`;
  return picture;
};

// Prefer thumbnail (200x200) when available, fallback to full picture
const resolveAvatarUrl = (user) => {
  if (!user) return null;
  return resolvePictureUrl(user.picture_thumb || user.picture);
};

// ==================== LANGUAGE & AUTH CONTEXTS ====================
// Migrated to /contexts/. Re-import here so the rest of App.js keeps working.
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// ==================== PUSH NOTIFICATIONS ====================
const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    // Auto-subscribe when user logs in and permission is granted
    if (user && isSupported && permission === 'granted') {
      checkSubscription();
    }
  }, [user, isSupported, permission]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  const subscribe = async () => {
    if (!isSupported) {
      console.warn('Push notifications not supported');
      return false;
    }

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const response = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = response.data.publicKey;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      });

      setIsSubscribed(true);
      console.log('Push notification subscription successful');
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return false;
    }
  };

  const unsubscribe = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from backend
        await axios.post(`${API}/push/unsubscribe`, {
          subscription: subscription.toJSON()
        });
        
        // Unsubscribe from browser
        await subscription.unsubscribe();
      }
      
      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  };

  const testNotification = async () => {
    try {
      await axios.post(`${API}/push/test`);
      return true;
    } catch (error) {
      console.error('Error sending test notification:', error);
      return false;
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    subscribe,
    unsubscribe,
    testNotification
  };
};

// ==================== AUTH CALLBACK ====================

// ==================== PWA INSTALL PROMPT ====================
const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return false;

    try {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      
      setInstallPrompt(null);
      return outcome === 'accepted';
    } catch (error) {
      console.error('Error installing PWA:', error);
      return false;
    }
  };

  const dismiss = () => {
    setIsInstallable(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  return { isInstallable, isInstalled, install, dismiss };
};

// Install Banner Component
const PWAInstallBanner = () => {
  const { isInstallable, install, dismiss } = usePWAInstall();
  const { lang } = useLanguage();
  const [show, setShow] = useState(true);

  useEffect(() => {
    // Check if user dismissed the banner before
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      setShow(false);
    }
  }, []);

  if (!isInstallable || !show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="flex items-start gap-3">
        <img src="/icons/icon-96x96.png" alt="LëtzServices" className="w-12 h-12 rounded-xl" />
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">
            {lang === "fr" ? "Installer LëtzServices" : "Install LëtzServices"}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {lang === "fr" 
              ? "Ajoutez l'app sur votre écran d'accueil pour un accès rapide" 
              : "Add the app to your home screen for quick access"}
          </p>
          <div className="flex gap-2 mt-3">
            <Button 
              onClick={install}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-sm"
              data-testid="pwa-install-btn"
            >
              {lang === "fr" ? "Installer" : "Install"}
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => { dismiss(); setShow(false); }}
              className="text-slate-500 rounded-xl text-sm"
            >
              {lang === "fr" ? "Plus tard" : "Later"}
            </Button>
          </div>
        </div>
        <button 
          onClick={() => { dismiss(); setShow(false); }}
          className="text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ==================== AUTH CALLBACK COMPONENT ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Try to get session_id from query params first, then from hash
      let sessionId = searchParams.get("session_id");
      
      if (!sessionId) {
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        sessionId = sessionIdMatch ? sessionIdMatch[1] : null;
      }

      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          setUser(response.data);
          localStorage.setItem("letzservices_user", JSON.stringify(response.data));
          toast.success("Connexion réussie !");
          navigate("/", { replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Erreur d'authentification");
          navigate("/auth", { replace: true });
        }
      } else {
        console.log("No session_id found in URL");
        navigate("/auth", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto mb-4" />
        <p className="text-slate-600">Connexion en cours...</p>
      </div>
    </div>
  );
};

// ==================== AUTH PAGE (Email + Google) ====================
const AuthPage = () => {
  const { t, lang } = useLanguage();
  const { setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login, register, verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login/email`, { email, password });
      setUser(response.data);
      localStorage.setItem("letzservices_user", JSON.stringify(response.data));
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("auth_wrong_credentials"));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate password: min 8 chars + special character
    if (password.length < 8) {
      setError(lang === "fr" ? "Le mot de passe doit contenir au moins 8 caractères" : "Password must be at least 8 characters");
      return;
    }
    
    const specialChars = "!@#$%^&*()_+-=[]{}|;:',.<>?/~`";
    if (!password.split('').some(c => specialChars.includes(c))) {
      setError(lang === "fr" ? "Le mot de passe doit contenir au moins un caractère spécial (!@#$%...)" : "Password must contain at least one special character (!@#$%...)");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register/email`, { email, password, name });
      setMode("verify");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors
      if (Array.isArray(detail)) {
        const msg = detail[0]?.msg || detail[0]?.message || t("auth_email_exists");
        setError(msg);
      } else {
        setError(typeof detail === "string" ? detail : t("auth_email_exists"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code: verificationCode });
      setUser(response.data);
      localStorage.setItem("letzservices_user", JSON.stringify(response.data));
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      toast.success(lang === "fr" ? "Code renvoyé !" : "Code resent!");
    } catch (err) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <span className="text-2xl font-bold text-sky-600 font-['Outfit']">LëtzServices</span>
          </div>
          
          {mode !== "verify" && (
            <div className="flex bg-slate-100 rounded-xl p-1 mb-4">
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  mode === "login" 
                    ? "bg-white text-sky-600 shadow-sm" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="tab-login"
              >
                {lang === "fr" ? "Connexion" : "Login"}
              </button>
              <button
                type="button"
                onClick={() => { setMode("register"); setError(""); }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  mode === "register" 
                    ? "bg-white text-sky-600 shadow-sm" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="tab-register"
              >
                {lang === "fr" ? "Inscription" : "Sign up"}
              </button>
            </div>
          )}
          
          {mode === "verify" && (
            <CardTitle className="text-2xl font-bold font-['Outfit']">
              {t("auth_verify_title")}
            </CardTitle>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {mode === "verify" ? (
            <form onSubmit={handleVerify} className="space-y-4">
              <p className="text-center text-slate-600">
                {t("auth_verify_desc")} <strong>{email}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t("auth_verify_code")}</label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t("auth_verify_code_placeholder")}
                  className="rounded-xl text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                  data-testid="verify-code"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 font-bold"
                data-testid="verify-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth_verify_btn")}
              </Button>
              <button
                type="button"
                onClick={handleResendCode}
                className="w-full text-center text-sky-600 hover:text-sky-700 text-sm"
              >
                {t("auth_resend_code")}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={mode === "login" ? handleEmailLogin : handleEmailRegister} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("auth_name")}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("auth_name_placeholder")}
                        className="rounded-xl pl-10"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("auth_email")}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("auth_email_placeholder")}
                      className="rounded-xl pl-10"
                      required
                      data-testid="auth-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("auth_password")}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("auth_password_placeholder")}
                      className="rounded-xl pl-10 pr-10"
                      required
                      data-testid="auth-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {mode === "register" && (
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="accept-terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-sky-500"
                      data-testid="accept-terms-checkbox"
                    />
                    <label htmlFor="accept-terms" className="text-sm text-slate-600">
                      {lang === "fr" ? (
                        <>
                          J'accepte les{" "}
                          <a href="/legal/terms-of-use" target="_blank" className="text-sky-600 hover:underline">
                            Conditions Générales d'Utilisation
                          </a>
                          , les{" "}
                          <a href="/legal/terms-of-sale" target="_blank" className="text-sky-600 hover:underline">
                            Conditions Générales de Vente
                          </a>
                          {" "}et la{" "}
                          <a href="/legal/privacy" target="_blank" className="text-sky-600 hover:underline">
                            Politique de Confidentialité
                          </a>
                        </>
                      ) : (
                        <>
                          I accept the{" "}
                          <a href="/legal/terms-of-use" target="_blank" className="text-sky-600 hover:underline">
                            Terms of Use
                          </a>
                          , the{" "}
                          <a href="/legal/terms-of-sale" target="_blank" className="text-sky-600 hover:underline">
                            Terms of Sale
                          </a>
                          {" "}and the{" "}
                          <a href="/legal/privacy" target="_blank" className="text-sky-600 hover:underline">
                            Privacy Policy
                          </a>
                        </>
                      )}
                    </label>
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={loading || (mode === "register" && !acceptedTerms)}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="auth-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === "login" ? t("auth_login_btn") : t("auth_register_btn"))}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-slate-500">{t("auth_or")}</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full rounded-xl py-6 border-slate-300"
                data-testid="google-login"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t("auth_google")}
              </Button>

            </>
          )}

          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full text-center text-slate-500 hover:text-slate-700 text-sm"
          >
            ← {t("back")}
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ==================== HEADER ====================
const Header = ({ scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { lang, toggleLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingRatings, setPendingRatings] = useState([]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch notification count
  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const response = await axios.get(`${API}/notifications/unread-count`);
        setUnreadCount(response.data.unread_messages);
        const ratingsResponse = await axios.get(`${API}/pending-ratings`);
        setPendingRatings(ratingsResponse.data);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
    // Poll every 60s (was 30s) and only when tab is visible
    const interval = setInterval(fetchNotifications, 60000);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchNotifications();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const navItems = isLandingPage
    ? [
        { label: t("nav_announcements"), id: "posts", action: () => navigate("/annonces") },
        { label: t("nav_marketplace"), id: "marketplace", action: () => navigate("/marketplace") },
        { label: t("nav_how_it_works"), id: "how-it-works", action: () => scrollToSection?.("how-it-works") },
        { label: t("nav_services"), id: "services", action: () => scrollToSection?.("services") },
      ]
    : [
        { label: t("nav_announcements"), action: () => navigate("/annonces") },
        { label: t("nav_marketplace"), action: () => navigate("/marketplace") },
        { label: t("nav_dashboard"), action: () => navigate("/dashboard") },
        ...(user?.is_admin ? [{ label: "Admin", action: () => navigate("/admin"), isAdmin: true }] : []),
      ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "header-glass bg-white/80 border-b border-slate-200/50 shadow-sm" : "bg-white/90"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a
            href="/"
            data-testid="logo"
            className="flex items-center space-x-2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Outfit']">LëtzServices</span>
          </a>

          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item, idx) => (
              <button
                key={idx}
                data-testid={`nav-${item.id || item.label.toLowerCase()}`}
                onClick={item.action}
                className={`transition-colors font-medium ${
                  item.isAdmin 
                    ? "text-sky-600 hover:text-sky-700 flex items-center gap-1" 
                    : "text-slate-600 hover:text-sky-600"
                }`}
              >
                {item.isAdmin && <Shield className="w-4 h-4" />}
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-sky-600 border border-slate-200 rounded-lg hover:border-sky-300 transition-all"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
            
            {user ? (
              <>
                <button
                  onClick={() => navigate("/messages")}
                  className="relative p-2 text-slate-600 hover:text-sky-600"
                  data-testid="messages-btn"
                >
                  <MessageSquare className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                {pendingRatings.length > 0 && (
                  <button
                    onClick={() => navigate("/pending-ratings")}
                    className="relative p-2 text-slate-600 hover:text-yellow-600"
                    data-testid="pending-ratings-btn"
                    title={lang === "fr" ? "Notations en attente" : "Pending ratings"}
                  >
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingRatings.length}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center space-x-2"
                  data-testid="profile-btn"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.picture?.startsWith("letzservices/") ? `${API}/files/${user.picture}` : user.picture} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                </button>
                <Button
                  variant="outline"
                  onClick={logout}
                  className="border-slate-200"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("nav_logout")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="header-login"
                  variant="outline"
                  onClick={login}
                  className="border-sky-600 text-sky-600 hover:bg-sky-50 rounded-xl px-5 py-2 font-medium"
                >
                  {t("nav_login")}
                </Button>
                <Button
                  data-testid="header-cta"
                  onClick={login}
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-6 py-2 font-bold shadow-lg shadow-sky-600/20"
                >
                  {t("nav_create_account")}
                </Button>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-button"
            aria-label={t("nav_menu") || "Menu"}
            className="md:hidden relative p-2 -mr-2 touch-manipulation"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            {!isMobileMenuOpen && user && (unreadCount > 0 || pendingRatings.length > 0) && (
              <span
                data-testid="mobile-menu-badge"
                className="absolute top-1 right-1 bg-red-500 rounded-full w-2.5 h-2.5 border-2 border-white"
              />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-4 py-4 space-y-1">
              {/* User profile summary (connected) */}
              {user && (
                <button
                  data-testid="mobile-profile-summary"
                  onClick={() => {
                    navigate("/profile");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 bg-sky-50 rounded-xl mb-2 hover:bg-sky-100 transition-colors"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user.picture?.startsWith("letzservices/") ? `${API}/files/${user.picture}` : user.picture} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{t("nav_view_profile") || "Voir mon profil"}</div>
                  </div>
                </button>
              )}

              {/* Main navigation */}
              {navItems.map((item, idx) => (
                <button
                  key={idx}
                  data-testid={`mobile-nav-${item.id || item.label.toLowerCase()}`}
                  onClick={() => {
                    item.action();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-2 transition-colors ${
                    item.isAdmin
                      ? "text-sky-600 bg-sky-50 hover:bg-sky-100"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {item.isAdmin && <Shield className="w-4 h-4" />}
                  {item.label}
                </button>
              ))}

              {/* Connected-user shortcuts */}
              {user && (
                <>
                  <div className="border-t border-slate-100 my-2"></div>

                  <button
                    data-testid="mobile-create-post"
                    onClick={() => {
                      navigate("/posts/new");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <PlusCircle className="w-5 h-5 text-sky-600" />
                    {t("nav_create_post") || "Créer une annonce"}
                  </button>

                  <button
                    data-testid="mobile-dashboard"
                    onClick={() => {
                      navigate("/dashboard");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <LayoutDashboard className="w-5 h-5 text-slate-500" />
                    {t("nav_dashboard") || "Tableau de bord"}
                  </button>

                  <button
                    data-testid="mobile-messages"
                    onClick={() => {
                      navigate("/messages");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <MessageSquare className="w-5 h-5 text-slate-500" />
                    <span className="flex-1">{t("nav_messages") || "Messages"}</span>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>

                  <button
                    data-testid="mobile-favorites"
                    onClick={() => {
                      navigate("/favorites");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <Heart className="w-5 h-5 text-slate-500" />
                    {t("nav_favorites") || "Favoris"}
                  </button>

                  {pendingRatings.length > 0 && (
                    <button
                      data-testid="mobile-pending-ratings"
                      onClick={() => {
                        navigate("/pending-ratings");
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                    >
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="flex-1">{t("nav_pending_ratings") || "Notations en attente"}</span>
                      <span className="bg-yellow-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                        {pendingRatings.length}
                      </span>
                    </button>
                  )}

                  <button
                    data-testid="mobile-profile"
                    onClick={() => {
                      navigate("/profile");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
                  >
                    <User className="w-5 h-5 text-slate-500" />
                    {t("nav_profile") || "Mon profil"}
                  </button>
                </>
              )}

              <div className="border-t border-slate-100 my-2"></div>

              {/* Language toggle - always visible */}
              <button
                data-testid="mobile-lang-toggle"
                onClick={() => {
                  toggleLang();
                }}
                className="w-full text-left font-medium py-3 px-3 rounded-lg flex items-center gap-3 text-slate-700 hover:bg-slate-50"
              >
                <Globe className="w-5 h-5 text-slate-500" />
                <span className="flex-1">
                  {lang === "fr" ? "Langue : Français" : "Language: English"}
                </span>
                <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2 py-1 rounded">
                  {lang === "fr" ? "→ EN" : "→ FR"}
                </span>
              </button>

              {/* Auth buttons */}
              {user ? (
                <Button
                  data-testid="mobile-logout-btn"
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full mt-2"
                  variant="outline"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("nav_logout")}
                </Button>
              ) : (
                <div className="space-y-2 pt-2">
                  <Button
                    data-testid="mobile-login-btn"
                    onClick={() => {
                      login();
                      setIsMobileMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full border-sky-600 text-sky-600"
                  >
                    {t("nav_login")}
                  </Button>
                  <Button
                    data-testid="mobile-signup-btn"
                    onClick={() => {
                      login();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {t("nav_create_account")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== SIGNUP POPUP ====================
const SignupPopup = ({ onClose, onSignup }) => {
  const { t } = useLanguage();
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="signup-popup"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-sky-600" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">
            {t("popup_title")}
          </h3>
          <p className="text-slate-600 mb-6">
            {t("popup_description")}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={onSignup}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-4 font-bold"
              data-testid="popup-signup-btn"
            >
              {t("popup_cta")}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <button 
              onClick={onClose}
              className="w-full text-slate-500 hover:text-slate-700 py-2 text-sm"
              data-testid="popup-close-btn"
            >
              {t("popup_later")}
            </button>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="flex justify-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-sky-600" />
                {t("popup_free")}
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-sky-600" />
                {t("popup_secure")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const { user, login } = useAuth();
  const { lang } = useLanguage();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Show popup after 5 seconds if user is not logged in
    if (!user) {
      const timer = setTimeout(() => {
        // Check if popup was already shown in this session
        const popupShown = sessionStorage.getItem('signupPopupShown');
        if (!popupShown) {
          setShowPopup(true);
          sessionStorage.setItem('signupPopupShown', 'true');
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <>
      <SEO 
        title={lang === "fr" ? "Services et Marketplace au Luxembourg" : "Services and Marketplace in Luxembourg"}
        description={lang === "fr" 
          ? "Trouvez ou proposez des services entre particuliers au Luxembourg. Ménage, bricolage, déménagement, cours particuliers. 100% gratuit et local."
          : "Find or offer services between individuals in Luxembourg. Cleaning, DIY, moving, tutoring. 100% free and local."}
        url="/"
      />
      <Header scrollToSection={scrollToSection} />
      {showPopup && !user && (
        <SignupPopup 
          onClose={() => setShowPopup(false)} 
          onSignup={() => {
            setShowPopup(false);
            login();
          }}
        />
      )}
      <main>
        <HeroSection login={login} />
        <LetzVerkafenSection />
        <HowItWorksSection />
        <ServicesSection />
        <WhyChooseUsSection />
        <AboutSection />
      </main>
      <Footer />
    </>
  );
};

// ==================== LUXEMBOURG NEWS COMPONENT ====================
const LuxembourgFact = ({ lang }) => {
  const [news, setNews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Fallback news if database is empty
  const fallbackNews = [
    { source_fr: "🇱🇺 Actu Lux", source_en: "🇱🇺 Lux News", text_fr: "Les transports publics gratuits fêtent leurs 5 ans !", text_en: "Free public transport celebrates 5 years!" },
    { source_fr: "💼 Économie", source_en: "💼 Economy", text_fr: "Le Luxembourg reste le pays au plus haut salaire minimum en Europe.", text_en: "Luxembourg remains the country with highest minimum wage in Europe." },
  ];
  
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        if (response.data && response.data.length > 0) {
          setNews(response.data);
        }
      } catch (error) {
        console.log("Using fallback news");
      }
    };
    fetchNews();
  }, []);
  
  useEffect(() => {
    const displayNews = news.length > 0 ? news : fallbackNews;
    if (displayNews.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % displayNews.length);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [news.length]);
  
  const displayNews = news.length > 0 ? news : fallbackNews;
  const current = displayNews[currentIndex % displayNews.length];
  
  const source = lang === "fr" ? (current.source_fr || current.source_en || "🇱🇺 Actu") : (current.source_en || current.source_fr || "🇱🇺 News");
  const text = lang === "fr" ? (current.text_fr || current.text_en || "") : (current.text_en || current.text_fr || "");
  
  return (
    <div className="space-y-1">
      <p className="text-xs text-sky-300 font-medium">{source}</p>
      <p className="text-white text-sm">{text}</p>
    </div>
  );
};

// ==================== HERO SECTION ====================
const HeroSection = ({ login }) => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_members: 0, total_posts: 0 });
  const [favorites, setFavorites] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    if (user) {
      const fetchFavorites = async () => {
        try {
          const response = await axios.get(`${API}/favorites`);
          setFavorites(response.data);
        } catch (error) {
          console.error("Error fetching favorites:", error);
        }
      };
      const fetchRecent = async () => {
        try {
          const response = await axios.get(`${API}/posts?limit=4`);
          setRecentPosts(response.data.slice(0, 4));
        } catch (error) {
          console.error("Error fetching recent posts:", error);
        }
      };
      fetchFavorites();
      fetchRecent();
    }
  }, [user]);

  // Tips for entrepreneurs in Luxembourg
  const tips = lang === "fr" ? [
    { icon: "📸", title: "Profil", text: "Un profil avec photo inspire plus confiance." },
    { icon: "⚡", title: "Réactivité", text: "Répondez rapidement pour ne pas perdre des clients." },
    { icon: "📝", title: "Description", text: "Une description claire attire plus de demandes." },
    { icon: "💬", title: "Communication", text: "Soyez poli et professionnel dans vos échanges." },
    { icon: "💸", title: "Prix", text: "Indiquez des prix clairs pour rassurer les clients." },
    { icon: "📍", title: "Localisation", text: "Précisez votre zone pour être contacté plus facilement." },
    { icon: "🛠", title: "Portfolio", text: "Ajoutez des photos de vos réalisations." },
    { icon: "🔍", title: "Clarté", text: "Un profil clair donne moins d'hésitation au client." },
    { icon: "📈", title: "Activité", text: "Plus tu es actif, plus tu reçois de demandes." },
    { icon: "🤝", title: "Fidélisation", text: "Un client satisfait peut te ramener plusieurs clients." },
    { icon: "⭐", title: "Avis", text: "Demandez un avis après chaque prestation." },
    { icon: "🔄", title: "Visibilité", text: "Restez actif pour apparaître plus souvent." },
    { icon: "💡", title: "Honnêteté", text: "Sois honnête sur ce que tu peux faire (et ne pas faire)." },
    { icon: "🚀", title: "Premiers avis", text: "Les premiers avis sont les plus importants, demande-les !" },
  ] : [
    { icon: "📸", title: "Profile", text: "A profile with a photo inspires more trust." },
    { icon: "⚡", title: "Responsiveness", text: "Respond quickly to not lose clients." },
    { icon: "📝", title: "Description", text: "A clear description attracts more requests." },
    { icon: "💬", title: "Communication", text: "Be polite and professional in your exchanges." },
    { icon: "💸", title: "Pricing", text: "Indicate clear prices to reassure clients." },
    { icon: "📍", title: "Location", text: "Specify your area to be contacted more easily." },
    { icon: "🛠", title: "Portfolio", text: "Add photos of your work." },
    { icon: "🔍", title: "Clarity", text: "A clear profile gives clients less hesitation." },
    { icon: "📈", title: "Activity", text: "The more active you are, the more requests you receive." },
    { icon: "🤝", title: "Loyalty", text: "A satisfied client can bring you several more clients." },
    { icon: "⭐", title: "Reviews", text: "Ask for a review after each service." },
    { icon: "🔄", title: "Visibility", text: "Stay active to appear more often." },
    { icon: "💡", title: "Honesty", text: "Be honest about what you can (and can't) do." },
    { icon: "🚀", title: "First reviews", text: "The first reviews are the most important, ask for them!" },
  ];

  const randomTip = tips[Math.floor(Math.random() * tips.length)];

  const sendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSendingFeedback(true);
    try {
      // Send feedback as a message to admin account
      await axios.post(`${API}/feedback`, {
        message: feedbackText,
        user_name: user.name,
        user_email: user.email
      });
      toast.success(lang === "fr" ? "Merci pour votre avis ! 💙" : "Thank you for your feedback! 💙");
      setFeedbackText("");
      setShowFeedback(false);
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur lors de l'envoi" : "Error sending feedback");
    } finally {
      setSendingFeedback(false);
    }
  };

  // If user is logged in, show personalized hero
  if (user) {
    return (
      <section data-testid="hero-section" className="relative min-h-screen flex items-center bg-gradient-to-br from-sky-900 via-sky-800 to-slate-900 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24 w-full">
          {/* Welcome Message */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-['Outfit'] mb-4">
              {lang === "fr" ? `Bienvenue, ${user.name} !` : `Welcome, ${user.name}!`}
            </h1>
            <p className="text-xl sm:text-2xl text-sky-200 mb-6">
              {lang === "fr" 
                ? "Merci de faire confiance à LëtzServices 💙" 
                : "Thank you for trusting LëtzServices 💙"}
            </p>
            
            {/* Feedback Button */}
            <button
              onClick={() => setShowFeedback(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all text-sm"
            >
              <MessageSquare className="w-4 h-4" />
              {lang === "fr" ? "Donnez-nous votre avis" : "Give us your feedback"}
            </button>
          </div>

          {/* Feedback Modal */}
          {showFeedback && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-slate-900 font-['Outfit']">
                    {lang === "fr" ? "Votre avis compte !" : "Your feedback matters!"}
                  </h3>
                  <button onClick={() => setShowFeedback(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-600 mb-4">
                  {lang === "fr" 
                    ? "Aidez-nous à améliorer LëtzServices. Partagez vos suggestions, idées ou problèmes rencontrés." 
                    : "Help us improve LëtzServices. Share your suggestions, ideas, or issues you've encountered."}
                </p>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={lang === "fr" ? "Écrivez votre message ici..." : "Write your message here..."}
                  rows={4}
                  className="rounded-xl mb-4"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={sendFeedback}
                    disabled={sendingFeedback || !feedbackText.trim()}
                    className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                  >
                    {sendingFeedback ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {lang === "fr" ? "Envoyer" : "Send"}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowFeedback(false)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tip Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{randomTip.icon}</span>
                <h3 className="text-lg font-bold text-white">{randomTip.title}</h3>
              </div>
              <p className="text-sky-100 leading-relaxed">{randomTip.text}</p>
              
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm text-sky-300 mb-2">
                  🇱🇺 {lang === "fr" ? "Le saviez-vous ?" : "Did you know?"}
                </p>
                <LuxembourgFact lang={lang} />
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                {lang === "fr" ? "Actions rapides" : "Quick Actions"}
              </h3>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/posts/new")}
                  className="w-full bg-white text-sky-900 hover:bg-sky-50 rounded-xl justify-start"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {lang === "fr" ? "Publier une annonce" : "Post a listing"}
                </Button>
                <Button 
                  onClick={() => navigate("/marketplace/new")}
                  className="w-full bg-white/20 text-white hover:bg-white/30 rounded-xl justify-start"
                >
                  <Package className="w-4 h-4 mr-2" />
                  {lang === "fr" ? "Vendre un article" : "Sell an item"}
                </Button>
                <Button 
                  onClick={() => navigate("/favorites")}
                  className="w-full bg-white/20 text-white hover:bg-white/30 rounded-xl justify-start"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  {lang === "fr" ? "Mes favoris" : "My favorites"} 
                  {favorites.length > 0 && <Badge className="ml-auto bg-red-500 text-white">{favorites.length}</Badge>}
                </Button>
                <Button 
                  onClick={() => navigate("/messages")}
                  className="w-full bg-white/20 text-white hover:bg-white/30 rounded-xl justify-start"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {lang === "fr" ? "Mes messages" : "My messages"}
                </Button>
              </div>
            </div>

            {/* Community Stats Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-sky-400" />
                {lang === "fr" ? "Notre communauté" : "Our Community"}
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 bg-white/10 rounded-xl">
                  <p className="text-2xl font-bold text-white">{stats.total_members || "—"}</p>
                  <p className="text-sm text-sky-200">{lang === "fr" ? "Membres" : "Members"}</p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-xl">
                  <p className="text-2xl font-bold text-white">{stats.total_posts || "—"}</p>
                  <p className="text-sm text-sky-200">{lang === "fr" ? "Annonces" : "Listings"}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sky-100">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>{lang === "fr" ? "100% gratuit, toujours" : "100% free, always"}</span>
                </div>
                <div className="flex items-center gap-3 text-sky-100">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span>{lang === "fr" ? "Communauté vérifiée" : "Verified community"}</span>
                </div>
                <div className="flex items-center gap-3 text-sky-100">
                  <MapPin className="w-5 h-5 text-green-400" />
                  <span>{lang === "fr" ? "100% luxembourgeois" : "100% Luxembourgish"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          {recentPosts.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-sky-400" />
                {lang === "fr" ? "Dernières annonces" : "Latest Listings"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentPosts.map((post) => (
                  <div
                    key={post.post_id}
                    onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 cursor-pointer hover:bg-white/20 transition-all"
                  >
                    <Badge className={post.post_type === "demande" ? "bg-blue-500 text-white mb-2" : "bg-green-500 text-white mb-2"}>
                      {post.post_type === "demande" ? (lang === "fr" ? "Recherche" : "Looking for") : (lang === "fr" ? "Propose" : "Offering")}
                    </Badge>
                    <h4 className="font-semibold text-white truncate">{post.title}</h4>
                    <p className="text-sm text-sky-200 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {post.ville}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 scroll-indicator">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>
    );
  }

  // Default hero for non-logged-in users
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center bg-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-8">
            <div className="space-y-6">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">
                {t("hero_subtitle")}
              </span>
              <h1 className="text-5xl sm:text-6xl tracking-tight font-black text-slate-900 font-['Outfit'] leading-tight">
                {t("hero_title")}
              </h1>
              <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed">
                {t("hero_tagline")}
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-slate-500">
                {t("hero_description")}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-button"
                onClick={login}
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-8 py-6 font-bold text-lg shadow-lg shadow-sky-600/20"
              >
                {t("hero_cta")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                onClick={() => navigate("/annonces")}
                className="border-2 border-slate-200 hover:border-sky-600 hover:text-sky-600 rounded-xl px-8 py-6 font-bold text-lg"
              >
                {t("hero_secondary_cta")}
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-slate-500">
                <CheckCircle className="w-5 h-5 text-sky-600" />
                <span className="text-sm font-medium">{t("hero_free")}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Shield className="w-5 h-5 text-sky-600" />
                <span className="text-sm font-medium">{t("hero_verified")}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-5 h-5 text-sky-600" />
                <span className="text-sm font-medium">{t("hero_local")}</span>
              </div>
            </div>
          </div>

          {/* Right side - decorative element instead of image */}
          <div className="lg:col-span-6 relative">
            <div className="relative bg-gradient-to-br from-sky-50 to-sky-100 rounded-3xl p-12 shadow-lg">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <Wrench className="w-10 h-10 text-sky-600 mb-3" />
                  <p className="font-semibold text-slate-800">{t("service_bricolage")}</p>
                  <p className="text-sm text-slate-500">{t("service_bricolage_desc")}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <Sparkles className="w-10 h-10 text-sky-600 mb-3" />
                  <p className="font-semibold text-slate-800">{t("service_menage")}</p>
                  <p className="text-sm text-slate-500">{t("service_menage_desc")}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <Package className="w-10 h-10 text-sky-600 mb-3" />
                  <p className="font-semibold text-slate-800">{t("service_demenagement")}</p>
                  <p className="text-sm text-slate-500">{t("service_demenagement_desc")}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <Car className="w-10 h-10 text-sky-600 mb-3" />
                  <p className="font-semibold text-slate-800">{t("service_auto")}</p>
                  <p className="text-sm text-slate-500">{t("service_auto_desc")}</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-sky-200 rounded-full blur-2xl opacity-50 -z-10" />
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 scroll-indicator">
        <ChevronDown className="w-8 h-8 text-slate-400" />
      </div>
    </section>
  );
};

// ==================== ABOUT SECTION ====================
const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="about" data-testid="about-section" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative bg-gradient-to-br from-slate-100 to-slate-50 rounded-3xl p-8 shadow-lg">
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{t("about_network")}</p>
                    <p className="text-sm text-slate-500">{t("about_network_desc")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{t("about_trust")}</p>
                    <p className="text-sm text-slate-500">{t("about_trust_desc")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{t("about_price")}</p>
                    <p className="text-sm text-slate-500">{t("about_price_desc")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{t("about_title")}</span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 font-['Outfit']">
              {t("about_heading")}
            </h2>
            <div className="space-y-4 text-base sm:text-lg leading-relaxed text-slate-600">
              <p>{t("about_p1")}</p>
              <p>
                <strong className="text-slate-900">LëtzServices</strong> {t("about_p2")}
              </p>
              <p>
                {t("about_p3")}{" "}
                <strong className="text-sky-600">{t("about_word_of_mouth")}</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== HOW IT WORKS ====================
const HowItWorksSection = () => {
  const { t } = useLanguage();
  
  const steps = [
    { number: "01", title: t("how_step1_title"), description: t("how_step1_desc"), icon: Users },
    { number: "02", title: t("how_step2_title"), description: t("how_step2_desc"), icon: Send },
    { number: "03", title: t("how_step3_title"), description: t("how_step3_desc"), icon: Search },
    { number: "04", title: t("how_step4_title"), description: t("how_step4_desc"), icon: MessageSquare },
    { number: "05", title: t("how_step5_title"), description: t("how_step5_desc"), icon: CheckCircle },
    { number: "06", title: t("how_step6_title"), description: t("how_step6_desc"), icon: Star },
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{t("how_title")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 font-['Outfit']">
            {t("how_heading")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <Card key={index} className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-sky-200 transition-all overflow-hidden">
              <span className="step-number">{step.number}</span>
              <CardContent className="relative z-10 p-0">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 font-['Outfit'] mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== SERVICES SECTION (simplified) ====================
const ServicesSection = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const categories = [
    { icon: Home, name: lang === "fr" ? "Ménage & Nettoyage" : "Cleaning", color: "bg-blue-100 text-blue-600" },
    { icon: Wrench, name: lang === "fr" ? "Bricolage & Réparations" : "DIY & Repairs", color: "bg-orange-100 text-orange-600" },
    { icon: Truck, name: lang === "fr" ? "Déménagement" : "Moving", color: "bg-green-100 text-green-600" },
    { icon: Car, name: lang === "fr" ? "Transport" : "Transport", color: "bg-purple-100 text-purple-600" },
    { icon: Sparkles, name: lang === "fr" ? "Jardinage" : "Gardening", color: "bg-emerald-100 text-emerald-600" },
    { icon: Package, name: lang === "fr" ? "Livraison" : "Delivery", color: "bg-pink-100 text-pink-600" },
    { icon: Users, name: lang === "fr" ? "Garde d'enfants" : "Babysitting", color: "bg-yellow-100 text-yellow-600" },
    { icon: Plane, name: lang === "fr" ? "Cours & Formation" : "Tutoring", color: "bg-indigo-100 text-indigo-600" },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{t("services_title")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 font-['Outfit']">
            {t("services_heading")}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            {lang === "fr" 
              ? "Trouvez ou proposez des services dans toutes les catégories" 
              : "Find or offer services in all categories"}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((cat, index) => (
            <div
              key={index}
              onClick={() => navigate("/annonces")}
              className="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group"
            >
              <div className={`w-14 h-14 ${cat.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                <cat.icon className="w-7 h-7" />
              </div>
              <p className="font-medium text-slate-800">{cat.name}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Button 
            onClick={() => navigate("/annonces")}
            variant="outline"
            className="border-sky-600 text-sky-600 hover:bg-sky-50 rounded-xl px-6"
          >
            {lang === "fr" ? "Voir toutes les annonces" : "View all listings"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// ==================== WHY CHOOSE US ====================
const WhyChooseUsSection = () => {
  const { t } = useLanguage();
  
  const advantages = [
    { icon: CheckCircle, title: t("why_price"), description: t("why_price_desc") },
    { icon: Clock, title: t("why_fast"), description: t("why_fast_desc") },
    { icon: MapPin, title: t("why_local"), description: t("why_local_desc") },
    { icon: Settings, title: t("why_simple"), description: t("why_simple_desc") },
    { icon: Star, title: t("why_reviews"), description: t("why_reviews_desc") },
  ];

  return (
    <section data-testid="why-choose-us-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{t("why_title")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 font-['Outfit']">
            {t("why_heading")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((adv, index) => (
            <div
              key={index}
              className={`p-6 rounded-2xl ${index === 0 ? "bg-sky-600 text-white lg:row-span-2" : "bg-slate-50"}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${index === 0 ? "bg-white/20" : "bg-sky-100"}`}>
                <adv.icon className={`w-6 h-6 ${index === 0 ? "text-white" : "text-sky-600"}`} />
              </div>
              <h3 className={`text-xl font-semibold font-['Outfit'] mb-2 ${index === 0 ? "text-white" : "text-slate-800"}`}>
                {adv.title}
              </h3>
              <p className={index === 0 ? "text-sky-100" : "text-slate-600"}>{adv.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== LËTZVERKAFEN SECTION ====================
const LetzVerkafenSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_products: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/marketplace`);
        setStats(response.data);
      } catch (error) {
        console.error("Error:", error);
      }
    };
    fetchStats();
  }, []);

  const features = [
    { icon: MapPin, title: t("verkafen_feature1"), desc: t("verkafen_feature1_desc") },
    { icon: Camera, title: t("verkafen_feature2"), desc: t("verkafen_feature2_desc") },
    { icon: MessageSquare, title: t("verkafen_feature3"), desc: t("verkafen_feature3_desc") },
  ];

  return (
    <section data-testid="verkafen-section" className="py-24 bg-gradient-to-br from-sky-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">{t("verkafen_section_title")}</span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 font-['Outfit']">
              {t("verkafen_section_heading")}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t("verkafen_section_description")}
            </p>
            <div className="space-y-4">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{feature.title}</p>
                    <p className="text-sm text-slate-500">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button
              onClick={() => navigate("/marketplace")}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-8 py-6 font-bold text-lg shadow-lg shadow-sky-600/20"
              data-testid="verkafen-cta"
            >
              {t("verkafen_cta")}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <div className="relative">
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square bg-slate-100 rounded-2xl flex items-center justify-center">
                  <Package className="w-16 h-16 text-slate-300" />
                </div>
                <div className="aspect-square bg-slate-100 rounded-2xl flex items-center justify-center">
                  <Package className="w-16 h-16 text-slate-300" />
                </div>
                <div className="aspect-square bg-slate-100 rounded-2xl flex items-center justify-center">
                  <Package className="w-16 h-16 text-slate-300" />
                </div>
                <div className="aspect-square bg-sky-600 rounded-2xl flex items-center justify-center text-white">
                  <div className="text-center">
                    <p className="text-3xl font-bold">{stats.total_products || 0}</p>
                    <p className="text-sm text-sky-100">{t("marketplace_filter_available")}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-sky-200 rounded-full blur-2xl opacity-50 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  
  return (
    <footer data-testid="footer" className="bg-slate-950 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-sky-600 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white font-['Outfit']">LëtzServices</span>
            </div>
            <p className="text-slate-400 leading-relaxed max-w-sm">
              {lang === "fr" 
                ? "La plateforme 100% gratuite de mise en relation entre particuliers et prestataires de services au Luxembourg."
                : "The 100% free platform connecting individuals and service providers in Luxembourg."}
            </p>
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-4 h-4 text-sky-500" />
              <span className="text-sm">100% Luxembourgeois</span>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit']">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => navigate("/annonces")} className="text-slate-400 hover:text-sky-400 transition-colors">
                  {lang === "fr" ? "Annonces" : "Listings"}
                </button>
              </li>
              <li>
                <button onClick={() => navigate("/marketplace")} className="text-slate-400 hover:text-sky-400 transition-colors">
                  Marketplace
                </button>
              </li>
              <li>
                <button onClick={() => navigate("/about")} className="text-slate-400 hover:text-sky-400 transition-colors">
                  {lang === "fr" ? "À propos" : "About"}
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit']">{lang === "fr" ? "Suivez-nous" : "Follow us"}</h4>
            <ul className="space-y-3">
              <li>
                <a href="https://www.instagram.com/letzservices?igsh=d3h2NTRvM3lpa3Jy" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-400 transition-colors flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  Instagram
                </a>
              </li>
              <li>
                <a href="https://www.tiktok.com/@letzservices.lu?_r=1&_t=ZN-959WwpmYiow" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                  TikTok
                </a>
              </li>
              <li className="text-slate-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Luxembourg
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} LëtzServices. {lang === "fr" ? "Tous droits réservés." : "All rights reserved."}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <button onClick={() => navigate("/legal/terms-of-use")} className="text-slate-500 hover:text-sky-400 transition-colors">
              {lang === "fr" ? "CGU" : "Terms of Use"}
            </button>
            <span className="text-slate-700">|</span>
            <button onClick={() => navigate("/legal/terms-of-sale")} className="text-slate-500 hover:text-sky-400 transition-colors">
              {lang === "fr" ? "CGV" : "Terms of Sale"}
            </button>
            <span className="text-slate-700">|</span>
            <button onClick={() => navigate("/legal/privacy")} className="text-slate-500 hover:text-sky-400 transition-colors">
              {lang === "fr" ? "Confidentialité" : "Privacy"}
            </button>
            <span className="text-slate-700">|</span>
            <button onClick={() => navigate("/legal/marketplace-rules")} className="text-slate-500 hover:text-sky-400 transition-colors">
              {lang === "fr" ? "Règles" : "Rules"}
            </button>
            <span className="text-slate-700">|</span>
            <a href="mailto:letzservicesinfo@gmail.com" className="text-slate-500 hover:text-sky-400 transition-colors">
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== POSTS PAGE ====================
const PostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchVille, setSearchVille] = useState("");
  const { user, login } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [filter, searchVille]);

  const fetchPosts = async () => {
    try {
      let url = `${API}/posts?status=active`;
      if (filter !== "all") url += `&post_type=${filter}`;
      if (searchVille) url += `&ville=${searchVille}`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = (post) => {
    if (!user) {
      toast.info(lang === "fr" ? "Connectez-vous pour contacter ce membre" : "Sign in to contact this member");
      login();
      return;
    }
    navigate(`/messages/new?userId=${post.user_id}&postId=${post.post_id}`);
  };

  return (
    <>
      <SEO 
        title={lang === "fr" ? "Annonces de Services" : "Service Listings"}
        description={lang === "fr" 
          ? "Parcourez les offres et demandes de services au Luxembourg. Ménage, bricolage, déménagement, cours et plus."
          : "Browse service offers and requests in Luxembourg. Cleaning, DIY, moving, tutoring and more."}
        url="/annonces"
      />
      <BreadcrumbSchema items={[
        { name: lang === "fr" ? "Accueil" : "Home", url: "/" },
        { name: lang === "fr" ? "Annonces" : "Listings", url: "/annonces" }
      ]} />
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">{t("posts_title")}</h1>
              <p className="text-slate-600 mt-1">{t("posts_subtitle")}</p>
            </div>
            {user && (
              <Button
                onClick={() => navigate("/posts/new")}
                className="mt-4 md:mt-0 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                data-testid="create-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("posts_create")}
              </Button>
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 mb-8 flex flex-col md:flex-row gap-4">
            <Tabs value={filter} onValueChange={setFilter} className="w-full md:w-auto">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all" data-testid="filter-all">{t("posts_filter_all")}</TabsTrigger>
                <TabsTrigger value="demande" data-testid="filter-demande">{t("posts_filter_demande")}</TabsTrigger>
                <TabsTrigger value="offre" data-testid="filter-offre">{t("posts_filter_offre")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t("posts_search_ville")}
                  value={searchVille}
                  onChange={(e) => setSearchVille(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-ville"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <p className="text-slate-500">{t("posts_no_results")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, idx) => (
                <PostCard key={post.post_id} post={post} index={idx} onContact={() => handleContact(post)} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

// ==================== POST CARD ====================
const PostCard = ({ post, onContact, index = 99 }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // First 3 cards above the fold → eager load + high priority for instant display
  const isPriority = index < 3;
  const imgLoading = isPriority ? "eager" : "lazy";

  const getUserPictureUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith("letzservices/")) {
      return `${API}/files/${picture}`;
    }
    return picture;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API}/files/${imagePath}`;
  };

  return (
    <Card
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all cursor-pointer"
      onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Photo thumbnail */}
      {post.photos && post.photos.length > 0 && (
        <div className="aspect-video bg-slate-100 overflow-hidden">
          <SafeImage 
            src={getImageUrl(post.photos[0])} 
            alt={post.title}
            loading={imgLoading}
            fetchpriority={isPriority ? "high" : "auto"}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <Badge variant={post.post_type === "demande" ? "default" : "secondary"} className={post.post_type === "demande" ? "bg-blue-100 text-blue-700" : "bg-sky-100 text-sky-700"}>
            {post.post_type === "demande" ? t("posts_type_recherche") : t("posts_type_propose")}
          </Badge>
          {post.photos && post.photos.length > 1 && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Camera className="w-3 h-3" />
              {post.photos.length}
            </span>
          )}
        </div>
        <CardTitle className="text-lg font-semibold text-slate-900 mt-2 line-clamp-2">{post.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-slate-600 text-sm line-clamp-2 mb-4">{post.description}</p>
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${post.user_id}`);
            }}
          >
            <Avatar className="w-8 h-8">
              <AvatarImage src={getUserPictureUrl(post.user_picture)} />
              <AvatarFallback>{post.user_name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-slate-900 hover:text-sky-600">{post.user_name}</p>
                {post.user_rating > 0 && (
                  <span className="flex items-center gap-0.5 text-xs text-yellow-600">
                    <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                    {post.user_rating.toFixed(1)}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {post.ville}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onContact();
            }}
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg"
            data-testid={`contact-btn-${post.post_id}`}
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
        </div>
        {post.price && <p className="text-sky-600 font-semibold mt-3">{formatPrice(post.price)}</p>}
      </CardContent>
    </Card>
  );
};

// ==================== POST DETAIL PAGE ====================
const PostDetailPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { user, login } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPost();
  }, [postId]);

  useEffect(() => {
    // Check favorite only after post is loaded (to use real post_id)
    if (user && post?.post_id) {
      checkFavorite();
    }
  }, [user, post?.post_id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`);
      setPost(response.data);
      // Fetch user reviews
      const reviewsRes = await axios.get(`${API}/users/${response.data.user_id}/reviews`);
      setUserReviews(reviewsRes.data);
    } catch (error) {
      toast.error("Annonce non trouvée");
      navigate("/annonces");
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      // Use post.post_id to ensure we check with the real ID, not the slug
      const idToCheck = post?.post_id || postId;
      const response = await axios.get(`${API}/favorites/check/post/${idToCheck}`);
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error("Error checking favorite:", error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.info(lang === "fr" ? "Connectez-vous pour sauvegarder" : "Sign in to save");
      login();
      return;
    }
    // Use the real post_id from the loaded post, not the URL param (which might be a slug)
    const realPostId = post?.post_id || postId;
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/post/${realPostId}`);
        setIsFavorite(false);
        toast.success(lang === "fr" ? "Retiré des favoris" : "Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, { post_id: realPostId });
        setIsFavorite(true);
        toast.success(lang === "fr" ? "Ajouté aux favoris" : "Added to favorites");
      }
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const openEditModal = () => {
    setEditForm({
      title: post.title || "",
      description: post.description || "",
      category: post.category || "",
      ville: post.ville || "",
      price: post.price || "",
      post_type: post.post_type || "offre"
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await axios.put(`${API}/posts/${post.post_id}`, editForm);
      toast.success(lang === "fr" ? "Annonce modifiée !" : "Post updated!");
      setShowEditModal(false);
      fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleContact = () => {
    if (!user) {
      toast.info("Connectez-vous pour contacter ce membre");
      login();
      return;
    }
    navigate(`/messages/new?userId=${post.user_id}&postId=${post.post_id}`);
  };

  const openCompletedModal = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      const postConversations = response.data.filter(
        (conv) => conv.post_id === postId
      );
      const potentialClients = postConversations.map((conv) => ({
        user_id: conv.other_user.user_id,
        name: conv.other_user.name,
        picture: conv.other_user.picture,
      }));
      setClients(potentialClients);
      setShowCompletedModal(true);
    } catch (error) {
      console.error("Error fetching clients:", error);
      handleMarkCompleted(null);
    }
  };

  const handleMarkCompleted = async (clientId) => {
    try {
      await axios.patch(`${API}/posts/${postId}/complete`, { 
        client_id: clientId 
      });
      setShowCompletedModal(false);
      toast.success(
        lang === "fr" 
          ? clientId ? "Prestation terminée ! Le client pourra vous noter." : "Prestation terminée"
          : clientId ? "Service completed! The client can now rate you." : "Service completed"
      );
      fetchPost();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const getUserPictureUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith("http")) return picture;
    return `${API}/files/${picture}`;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API}/files/${imagePath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!post) return null;

  const avgRating = userReviews.length > 0 ? (userReviews.reduce((sum, r) => sum + r.rating, 0) / userReviews.length).toFixed(1) : null;

  return (
    <>
      <SEO 
        title={post.title}
        description={post.description?.substring(0, 160)}
        image={post.photos?.[0] ? getImageUrl(post.photos[0]) : null}
        url={`/posts/${post.slug || post.post_id}`}
        type="article"
      />
      <ServiceSchema service={post} />
      <BreadcrumbSchema items={[
        { name: lang === "fr" ? "Accueil" : "Home", url: "/" },
        { name: lang === "fr" ? "Annonces" : "Listings", url: "/annonces" },
        { name: post.title, url: `/posts/${post.slug || post.post_id}` }
      ]} />
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>

          <Card className="bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <Badge variant={post.post_type === "demande" ? "default" : "secondary"} className={post.post_type === "demande" ? "bg-blue-100 text-blue-700" : "bg-sky-100 text-green-700"}>
                {post.post_type === "demande" ? "Recherche un service" : "Propose un service"}
              </Badge>

              <div className="flex items-start justify-between mt-4">
                <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{post.title}</h1>
                <button
                  onClick={toggleFavorite}
                  className={`p-2 rounded-full transition-colors ${isFavorite ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400 hover:text-red-500"}`}
                  data-testid="favorite-btn"
                >
                  <Heart className={`w-6 h-6 ${isFavorite ? "fill-current" : ""}`} />
                </button>
              </div>

              <div className="flex items-center gap-4 mt-4 text-slate-500 text-sm">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {post.ville}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(post.created_at).toLocaleDateString("fr-FR")}
                </span>
              </div>

              {post.price && <p className="text-2xl font-bold text-sky-600 mt-4">{formatPrice(post.price)}</p>}

              {/* Photos Gallery */}
              {post.photos && post.photos.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Photos ({post.photos.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {post.photos.map((photo, idx) => (
                      <div 
                        key={idx} 
                        className="aspect-square bg-slate-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightboxIndex(idx)}
                        data-testid={`post-photo-${idx}`}
                      >
                        <img 
                          src={getImageUrl(photo)} 
                          alt={`Photo ${idx + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{post.description}</p>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={resolvePictureUrl(post.user_picture)} />
                      <AvatarFallback>{post.user_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{post.user_name}</p>
                      {avgRating && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span>{avgRating}</span>
                          <span>({userReviews.length} avis)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {user?.user_id !== post.user_id && post.status !== "completed" && (
                    <Button onClick={handleContact} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl" data-testid="contact-user-btn">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contacter
                    </Button>
                  )}
                  {user?.user_id === post.user_id && post.status !== "completed" && (
                    <div className="flex gap-2">
                      <Button onClick={openEditModal} variant="outline" className="rounded-xl" data-testid="edit-post-btn">
                        <Pencil className="w-4 h-4 mr-2" />
                        {lang === "fr" ? "Modifier" : "Edit"}
                      </Button>
                      <Button onClick={openCompletedModal} className="bg-green-600 hover:bg-green-700 text-white rounded-xl" data-testid="mark-completed-btn">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {lang === "fr" ? "Terminé" : "Completed"}
                      </Button>
                    </div>
                  )}
                  {post.status === "completed" && (
                    <Badge className="bg-green-100 text-green-700">
                      {lang === "fr" ? "Terminé" : "Completed"}
                    </Badge>
                  )}
                </div>
              </div>

              {userReviews.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-4">Avis ({userReviews.length})</h3>
                  <div className="space-y-4">
                    {userReviews.slice(0, 3).map((review) => (
                      <div key={review.review_id} className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={review.reviewer_picture} />
                            <AvatarFallback>{review.reviewer_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">{review.reviewer_name}</p>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-3 h-3 ${i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`} />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />

      {/* Mark as Completed Modal */}
      {showCompletedModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {lang === "fr" ? "Marquer comme terminé" : "Mark as completed"}
            </h3>
            
            {clients.length > 0 ? (
              <>
                <p className="text-slate-600 mb-4">
                  {lang === "fr" 
                    ? "Sélectionnez le client pour qu'il puisse vous noter :" 
                    : "Select the client so they can rate you:"}
                </p>
                <div className="space-y-2 mb-4">
                  {clients.map((client) => (
                    <button
                      key={client.user_id}
                      onClick={() => setSelectedClient(client.user_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        selectedClient === client.user_id 
                          ? "border-green-500 bg-green-50" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={getUserPictureUrl(client.picture)} />
                        <AvatarFallback>{client.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{client.name}</span>
                      {selectedClient === client.user_id && (
                        <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleMarkCompleted(selectedClient)}
                    disabled={!selectedClient}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    {lang === "fr" ? "Confirmer" : "Confirm"}
                  </Button>
                  <Button
                    onClick={() => handleMarkCompleted(null)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    {lang === "fr" ? "Sans client" : "Without client"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-600 mb-4">
                  {lang === "fr" 
                    ? "Aucune conversation trouvée pour cette annonce. Confirmer la prestation ?" 
                    : "No conversations found for this post. Confirm the service?"}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleMarkCompleted(null)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    {lang === "fr" ? "Confirmer" : "Confirm"}
                  </Button>
                  <Button
                    onClick={() => setShowCompletedModal(false)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Edit Post Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {lang === "fr" ? "Modifier l'annonce" : "Edit post"}
            </h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {lang === "fr" ? "Titre" : "Title"}
                </label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {lang === "fr" ? "Description" : "Description"}
                </label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={4}
                  className="rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "Catégorie" : "Category"}
                  </label>
                  <Input
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "Ville" : "City"}
                  </label>
                  <Input
                    value={editForm.ville}
                    onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {lang === "fr" ? "Prix" : "Price"}
                </label>
                <Input
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  placeholder={lang === "fr" ? "Ex: 25€/h ou À négocier" : "Ex: 25€/h or Negotiable"}
                  className="rounded-xl"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                >
                  {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "Enregistrer" : "Save")}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  variant="outline"
                  className="rounded-xl"
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Lightbox for photos */}
      {lightboxIndex !== null && post.photos && (
        <ImageLightbox 
          images={post.photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          getImageUrl={getImageUrl}
        />
      )}
    </>
  );
};

// Import useParams
import { useParams } from "react-router-dom";

// ==================== CREATE POST PAGE ====================
const CreatePostPage = () => {
  const { t, lang } = useLanguage();
  const [formData, setFormData] = useState({
    post_type: "demande",
    title: "",
    description: "",
    category: "services",
    ville: "",
    code_postal: "",
    price: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Service categories for autocomplete
  const serviceCategories = lang === "fr" ? [
    "Ménage", "Nettoyage", "Repassage",
    "Bricolage", "Plomberie", "Électricité", "Peinture", "Montage meubles",
    "Déménagement", "Transport", "Livraison",
    "Jardinage", "Tonte pelouse", "Taille haies",
    "Cours particuliers", "Soutien scolaire", "Langues", "Musique",
    "Garde d'enfants", "Baby-sitting", "Aide aux devoirs",
    "Aide aux personnes âgées", "Accompagnement",
    "Informatique", "Réparation PC", "Installation",
    "Cuisine", "Traiteur", "Pâtisserie",
    "Coiffure", "Beauté", "Massage",
    "Photographie", "Vidéo", "Événementiel",
    "Autre"
  ] : [
    "Cleaning", "Housekeeping", "Ironing",
    "DIY", "Plumbing", "Electrical", "Painting", "Furniture assembly",
    "Moving", "Transport", "Delivery",
    "Gardening", "Lawn mowing", "Hedge trimming",
    "Tutoring", "School support", "Languages", "Music",
    "Childcare", "Babysitting", "Homework help",
    "Elderly care", "Companionship",
    "IT support", "PC repair", "Installation",
    "Cooking", "Catering", "Pastry",
    "Hairdressing", "Beauty", "Massage",
    "Photography", "Video", "Events",
    "Other"
  ];

  // Popular cities in Luxembourg
  const popularCities = [
    "Luxembourg-Ville", "Esch-sur-Alzette", "Differdange", "Dudelange",
    "Ettelbruck", "Diekirch", "Wiltz", "Echternach", "Remich", "Vianden"
  ];

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
    if (value.length >= 1) {
      const filtered = serviceCategories.filter(cat => 
        cat.toLowerCase().includes(value.toLowerCase())
      );
      setCategorySuggestions(filtered.slice(0, 8));
      setShowCategorySuggestions(filtered.length > 0);
    } else {
      setCategorySuggestions([]);
      setShowCategorySuggestions(false);
    }
  };

  const selectCategory = (category) => {
    setFormData({ ...formData, category });
    setShowCategorySuggestions(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 5 - selectedFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);
    
    if (filesToAdd.length > 0) {
      setSelectedFiles(prev => [...prev, ...filesToAdd]);
      const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviews]);
    }
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate description length
    if (formData.description.length < 10) {
      toast.error(lang === "fr" ? "La description doit faire au moins 10 caractères" : "Description must be at least 10 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      // Format price with € if provided (skip € for "gratuit", "free", etc.)
      const priceValue = formData.price?.toLowerCase().trim() || "";
      const isFreePrice = ["gratuit", "free", "gratis", "0", ""].includes(priceValue);
      const priceFormatted = formData.price ? 
        (isFreePrice ? formData.price : (formData.price.includes("€") ? formData.price : `${formData.price} €`)) : "";
      
      // Combine ville and code postal
      const villeComplete = formData.code_postal ? 
        `${formData.ville} (${formData.code_postal})` : formData.ville;
      
      // Create post first
      const response = await axios.post(`${API}/posts`, {
        ...formData,
        price: priceFormatted,
        ville: villeComplete,
      });
      
      // Upload photos if any
      if (selectedFiles.length > 0) {
        const photoFormData = new FormData();
        selectedFiles.forEach(file => {
          photoFormData.append('files', file);
        });
        
        try {
          await axios.post(`${API}/posts/${response.data.post_id}/images`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (photoError) {
          console.error("Photo upload error:", photoError);
          toast.warning(lang === "fr" ? "Annonce publiée mais erreur lors de l'upload des photos" : "Post created but photo upload failed");
        }
      }
      
      toast.success(lang === "fr" ? "Annonce publiée !" : "Listing posted!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Post creation error:", error.response?.data);
      const detail = error.response?.data?.detail;
      if (detail === "Non authentifié") {
        toast.error(lang === "fr" ? "Veuillez vous connecter pour publier" : "Please login to post");
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      } else {
        toast.error(lang === "fr" ? "Erreur lors de la publication" : "Error posting listing");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <Card className="bg-white rounded-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold font-['Outfit']">{t("create_title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_type_label")}</label>
                  <div className="flex gap-4">
                    <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all ${formData.post_type === "demande" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                      <input
                        type="radio"
                        name="post_type"
                        value="demande"
                        checked={formData.post_type === "demande"}
                        onChange={(e) => setFormData({ ...formData, post_type: e.target.value })}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <Search className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                        <p className="font-medium">{t("create_type_recherche")}</p>
                        <p className="text-sm text-slate-500">{t("create_type_recherche_desc")}</p>
                      </div>
                    </label>
                    <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all ${formData.post_type === "offre" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                      <input
                        type="radio"
                        name="post_type"
                        value="offre"
                        checked={formData.post_type === "offre"}
                        onChange={(e) => setFormData({ ...formData, post_type: e.target.value })}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <Wrench className="w-6 h-6 mx-auto mb-2 text-sky-600" />
                        <p className="font-medium">{t("create_type_propose")}</p>
                        <p className="text-sm text-slate-500">{t("create_type_propose_desc")}</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_title_label")}</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t("create_title_placeholder")}
                    required
                    className="rounded-xl"
                    data-testid="post-title"
                  />
                </div>

                {/* Category with autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {lang === "fr" ? "Type de service" : "Service type"}
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    onFocus={() => {
                      if (formData.category.length >= 1) {
                        setShowCategorySuggestions(categorySuggestions.length > 0);
                      } else {
                        setCategorySuggestions(serviceCategories.slice(0, 8));
                        setShowCategorySuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                    placeholder={lang === "fr" ? "Ex: Ménage, Bricolage, Cours..." : "Ex: Cleaning, DIY, Tutoring..."}
                    required
                    className="rounded-xl"
                    data-testid="post-category"
                  />
                  {showCategorySuggestions && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {categorySuggestions.map((cat, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectCategory(cat)}
                          className="w-full text-left px-4 py-2 hover:bg-sky-50 text-slate-700"
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_ville_label")}</label>
                    <Input
                      value={formData.ville}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, ville: value });
                        if (value.length >= 2) {
                          const results = searchCities(value);
                          setCitySuggestions(results.slice(0, 8));
                          setShowCitySuggestions(results.length > 0);
                        } else {
                          setShowCitySuggestions(false);
                        }
                      }}
                      onFocus={() => {
                        if (formData.ville.length < 2) {
                          setCitySuggestions(popularCities.map(c => ({ city: c, postalCode: "" })));
                          setShowCitySuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                      placeholder={t("create_ville_placeholder")}
                      required
                      className="rounded-xl"
                      data-testid="post-ville"
                    />
                    {showCitySuggestions && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {citySuggestions.map((city, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              const cityName = typeof city === 'string' ? city : city.city;
                              const postalCode = typeof city === 'string' ? '' : city.postalCode;
                              setFormData({ ...formData, ville: cityName, code_postal: postalCode });
                              setShowCitySuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-sky-50 flex justify-between items-center"
                          >
                            <span>{typeof city === 'string' ? city : city.city}</span>
                            {city.postalCode && <span className="text-sm text-slate-500">{city.postalCode}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_code_postal_label")}</label>
                    <Input
                      value={formData.code_postal}
                      onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                      placeholder={t("create_code_postal_placeholder")}
                      className="rounded-xl"
                      data-testid="post-code-postal"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_price_label")}</label>
                  <div className="relative">
                    <Input
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder={t("create_price_placeholder")}
                      className="rounded-xl pr-12"
                      data-testid="post-price"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">€</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t("create_price_hint")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_description_label")}</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t("create_description_placeholder")}
                    rows={5}
                    required
                    className="rounded-xl"
                    data-testid="post-description"
                  />
                </div>

                {/* Photos Section (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {lang === "fr" ? "Photos (optionnel)" : "Photos (optional)"}
                    <span className="text-slate-400 font-normal ml-2">({selectedFiles.length}/5)</span>
                  </label>
                  
                  {/* Image previews */}
                  {previewUrls.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mb-3">
                      {previewUrls.map((url, index) => (
                        <div key={index} className="relative aspect-square">
                          <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedFiles.length < 5 && (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-sky-500 transition-colors"
                    >
                      <Camera className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">
                        {lang === "fr" ? "Cliquez pour ajouter des photos" : "Click to add photos"}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {lang === "fr" ? "Maximum 5 photos" : "Maximum 5 photos"}
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 font-bold" data-testid="submit-post">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t("create_submit")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

// ==================== DASHBOARD PAGE ====================
const DashboardPage = () => {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [myPosts, setMyPosts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyPosts();
    fetchMyProducts();
    fetchConversations();
  }, []);

  const fetchMyPosts = async () => {
    try {
      const response = await axios.get(`${API}/my-posts`);
      setMyPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProducts = async () => {
    try {
      const response = await axios.get(`${API}/my-products`);
      setMyProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      // Calculate total unread messages across all conversations
      const totalUnread = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      console.log(`Dashboard: ${response.data.length} conversations, ${totalUnread} unread messages`);
      setUnreadMessages(totalUnread);
    } catch (error) {
      console.error("Error fetching conversations for dashboard:", error?.response?.data || error.message);
      setUnreadMessages(0);
    }
  };

  const handleStatusChange = async (postId, newStatus) => {
    try {
      await axios.patch(`${API}/posts/${postId}/status`, { status: newStatus });
      toast.success(lang === "fr" ? "Statut mis à jour" : "Status updated");
      fetchMyPosts();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t("delete_post_confirm"))) return;
    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success(t("deleted_success"));
      fetchMyPosts();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(t("delete_post_confirm"))) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success(t("deleted_success"));
      fetchMyProducts();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">{t("dashboard_title")}</h1>
              <p className="text-slate-600 mt-1">{t("dashboard_welcome")} {user?.name}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate("/messages")} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded-xl" data-testid="dashboard-messages">
                <MessageSquare className="w-4 h-4 mr-2" />
                {t("nav_messages")} {unreadMessages > 0 && <Badge className="ml-2 bg-purple-600">{unreadMessages}</Badge>}
              </Button>
              <Button onClick={() => navigate("/posts/new")} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl" data-testid="dashboard-create-post">
                <Plus className="w-4 h-4 mr-2" />
                {t("dashboard_new_post")}
              </Button>
              <Button onClick={() => navigate("/marketplace/new")} variant="outline" className="border-sky-300 text-sky-600 hover:bg-sky-50 rounded-xl" data-testid="dashboard-create-product">
                <Package className="w-4 h-4 mr-2" />
                {t("marketplace_create")}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white rounded-2xl p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/messages")}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{unreadMessages}</p>
                  <p className="text-slate-500 text-xs">{t("nav_messages")}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                  <Send className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{myPosts.length}</p>
                  <p className="text-slate-500 text-xs">{t("dashboard_my_posts")}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{myProducts.length}</p>
                  <p className="text-slate-500 text-xs">{t("marketplace_my_products")}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{user?.average_rating?.toFixed(1) || "—"}</p>
                  <p className="text-slate-500 text-xs">{t("dashboard_rating")}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Mes Annonces Services */}
          <Card className="bg-white rounded-2xl mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Outfit']">{t("dashboard_my_posts")}</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => navigate("/annonces")}>{lang === "fr" ? "Voir tout" : "View all"}</Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-600 mx-auto" />
                </div>
              ) : myPosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">{t("dashboard_no_posts")}</p>
                  <Button onClick={() => navigate("/posts/new")} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl">
                    {t("dashboard_create_first")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myPosts.slice(0, 5).map((post) => (
                    <div key={post.post_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="flex-1 cursor-pointer" onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={post.post_type === "demande" ? "default" : "secondary"} className={post.post_type === "demande" ? "bg-blue-100 text-blue-700" : "bg-sky-100 text-sky-700"}>
                            {post.post_type === "demande" ? (lang === "fr" ? "Demande" : "Request") : (lang === "fr" ? "Offre" : "Offer")}
                          </Badge>
                          <Badge variant="outline" className={post.status === "active" ? "border-green-500 text-green-600" : post.status === "completed" ? "border-blue-500 text-blue-600" : "border-slate-400"}>
                            {post.status === "active" ? t("dashboard_status_active") : post.status === "completed" ? t("dashboard_status_completed") : t("dashboard_status_cancelled")}
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-900">{post.title}</p>
                        <p className="text-sm text-slate-500">{post.ville} • {formatPrice(post.price)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {post.status === "active" && (
                          <Button size="sm" variant="ghost" onClick={() => handleStatusChange(post.post_id, "completed")} title={t("dashboard_mark_completed")}>
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePost(post.post_id)} title={t("delete_post")} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mes Produits Marketplace */}
          <Card className="bg-white rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Outfit']">{t("marketplace_my_products")}</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => navigate("/marketplace")}>{lang === "fr" ? "Voir tout" : "View all"}</Button>
            </CardHeader>
            <CardContent>
              {myProducts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">{lang === "fr" ? "Aucun produit en vente" : "No products for sale"}</p>
                  <Button onClick={() => navigate("/marketplace/new")} variant="outline" className="border-sky-300 text-sky-600 rounded-xl">
                    {t("marketplace_create")}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myProducts.slice(0, 6).map((product) => (
                    <div key={product.product_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="w-16 h-16 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                        {product.images?.[0] ? (
                          <SafeImage 
                            src={product.images[0].startsWith("http") ? product.images[0] : `${API}/files/${product.images[0]}`} 
                            alt="" 
                            className="w-full h-full object-cover"
                            fallback={<div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-slate-400" /></div>}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/marketplace/${product.slug || product.product_id}`)}>
                        <p className="font-medium text-slate-900 truncate">{product.title}</p>
                        <p className="text-sky-600 font-bold">{formatPrice(product.price)}</p>
                        <Badge variant="outline" className={product.status === "sold" ? "border-red-400 text-red-500" : "border-green-400 text-green-600"}>
                          {product.status === "sold" ? t("marketplace_status_sold") : t("marketplace_status_available")}
                        </Badge>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteProduct(product.product_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

// ==================== MESSAGES PAGE ====================
const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data || []);
    } catch (error) {
      console.error("Error fetching conversations:", error?.response?.data || error.message);
      // Retry once on failure
      if (retryCount < 1) {
        setTimeout(() => fetchConversations(retryCount + 1), 1000);
      } else {
        setConversations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit'] mb-8">{t("messages_title")}</h1>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />
            </div>
          ) : conversations.length === 0 ? (
            <Card className="bg-white rounded-2xl p-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t("messages_no_messages")}</p>
              <p className="text-sm text-slate-400 mt-2">{t("messages_start_conv")}</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {conversations.map((conv) => (
                <Card
                  key={conv.conversation_id}
                  className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => navigate(`/messages/${conv.conversation_id}`)}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={resolveAvatarUrl(conv.other_user)} />
                      <AvatarFallback>{conv.other_user?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-900">{conv.other_user?.name}</p>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-sky-600 text-white">{conv.unread_count}</Badge>
                        )}
                      </div>
                      {conv.post_title && <p className="text-sm text-sky-600 truncate">📌 {conv.post_title}</p>}
                      <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

// ==================== CONVERSATION PAGE ====================
const ConversationPage = () => {
  const { conversationId } = useParams();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const sendingRef = useRef(false); // Track sending state for polling
  const pendingMessagesRef = useRef(new Set()); // Track pending message IDs

  useEffect(() => {
    fetchConversation();
    fetchMessages();
    let interval = null;
    const startPolling = () => {
      if (interval) return;
      // Poll every 5s (reduced from 2s) to lower data usage on mobile
      interval = setInterval(() => {
        if (!sendingRef.current && document.visibilityState === "visible") {
          fetchMessages();
        }
      }, 5000);
    };
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // When tab becomes visible again, fetch immediately and resume polling
        fetchMessages();
        startPolling();
      } else {
        stopPolling();
      }
    };
    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversation = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      const conv = response.data.find(c => c.conversation_id === conversationId);
      setConversation(conv);
    } catch (error) {
      console.error("Error fetching conversation:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(prev => {
        const serverMessages = response.data;
        // Skip update if nothing changed (avoid unnecessary re-renders / scroll jump)
        const prevServerOnly = prev.filter(m => !m.pending);
        if (
          prevServerOnly.length === serverMessages.length &&
          prevServerOnly.every((m, i) => m.message_id === serverMessages[i].message_id && m.read === serverMessages[i].read)
        ) {
          return prev;
        }
        const pendingOptimistic = prev.filter(m => m.pending && pendingMessagesRef.current.has(m.message_id));
        const serverMsgIds = new Set(serverMessages.map(m => m.message_id));
        const uniqueOptimistic = pendingOptimistic.filter(m => !serverMsgIds.has(m.message_id));
        return [...serverMessages, ...uniqueOptimistic];
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage("");
    setSending(true);
    sendingRef.current = true;
    
    // Ajout optimiste du message (apparaît immédiatement)
    const optimisticId = `temp_${Date.now()}`;
    const optimisticMessage = {
      message_id: optimisticId,
      conversation_id: conversationId,
      sender_id: user.user_id,
      sender_name: user.name,
      sender_picture: user.picture,
      content: messageContent,
      created_at: new Date().toISOString(),
      read: false,
      pending: true
    };
    
    pendingMessagesRef.current.add(optimisticId);
    setMessages(prev => [...prev, optimisticMessage]);
    
    try {
      const response = await axios.post(`${API}/conversations/${conversationId}/messages`, { content: messageContent });
      // Remove from pending and update with real message
      pendingMessagesRef.current.delete(optimisticId);
      // Replace optimistic message with real one
      setMessages(prev => prev.map(m => 
        m.message_id === optimisticId 
          ? { ...response.data, pending: false }
          : m
      ));
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur lors de l'envoi" : "Error sending message");
      // Retirer le message optimiste en cas d'erreur
      pendingMessagesRef.current.delete(optimisticId);
      setMessages(prev => prev.filter(m => m.message_id !== optimisticId));
      setNewMessage(messageContent);
    } finally {
      setSending(false);
      // Small delay before re-enabling polling to let server sync
      setTimeout(() => {
        sendingRef.current = false;
      }, 500);
    }
  };

  const goToItem = () => {
    if (conversation?.product_id) {
      navigate(`/marketplace/${conversation.product_slug || conversation.product_id}`);
    } else if (conversation?.post_id) {
      navigate(`/posts/${conversation.post_slug || conversation.post_id}`);
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-4 min-h-screen bg-slate-50 flex flex-col">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex flex-col w-full">
          {/* Conversation Header */}
          {conversation && (
            <div className="bg-white rounded-t-2xl p-4 border-b flex items-center gap-4">
              <button onClick={() => navigate("/messages")} className="text-slate-500 hover:text-sky-600">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Avatar 
                className="w-10 h-10 cursor-pointer" 
                onClick={() => navigate(`/user/${conversation.other_user?.user_id}`)}
              >
                <AvatarImage src={resolveAvatarUrl(conversation.other_user)} />
                <AvatarFallback>{conversation.other_user?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p 
                  className="font-semibold text-slate-900 cursor-pointer hover:text-sky-600"
                  onClick={() => navigate(`/user/${conversation.other_user?.user_id}`)}
                >
                  {conversation.other_user?.name}
                </p>
                {conversation.post_title && (
                  <button 
                    onClick={goToItem}
                    className="text-sm text-sky-600 hover:underline flex items-center gap-1"
                  >
                    {conversation.product_id ? "🛒" : "📌"} {conversation.post_title}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
              {(conversation.post_id || conversation.product_id) && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={goToItem}
                  className="rounded-xl text-sky-600 border-sky-200 hover:bg-sky-50"
                >
                  {lang === "fr" ? "Voir l'annonce" : "View listing"}
                </Button>
              )}
            </div>
          )}

          {/* Messages */}
          <div className={`flex-1 overflow-y-auto bg-white p-4 ${!conversation ? 'rounded-t-2xl' : ''}`}>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-center text-slate-500 py-12">{t("start_conversation")}</p>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div key={msg.message_id} className={`flex ${msg.sender_id === "system" ? "justify-center" : msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                    {msg.sender_id === "system" ? (
                      /* System message (rating invitation, etc.) */
                      <div className="max-w-[85%] bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl px-5 py-4 text-center">
                        <p className="text-slate-800 whitespace-pre-line">{msg.content.replace(/\/pending-ratings/g, '')}</p>
                        <Button 
                          onClick={() => navigate("/pending-ratings")}
                          className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          {lang === "fr" ? "Noter maintenant" : "Rate now"}
                        </Button>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(msg.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    ) : (
                      /* Regular user message */
                      <div className={`max-w-[70%] ${msg.sender_id === user?.user_id ? "order-2" : ""}`}>
                        <div className={`rounded-2xl px-4 py-2 ${msg.sender_id === user?.user_id ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                          <p>{msg.content}</p>
                        </div>
                        <p className={`text-xs text-slate-400 mt-1 ${msg.sender_id === user?.user_id ? "text-right" : ""}`}>
                          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="bg-white rounded-b-2xl p-4 border-t flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t("message_placeholder")}
              className="flex-1 rounded-xl"
              data-testid="message-input"
            />
            <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl" data-testid="send-message">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </main>
    </>
  );
};

// ==================== NEW MESSAGE PAGE ====================
const NewMessagePage = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const postId = searchParams.get("postId");
  const productId = searchParams.get("productId");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [post, setPost] = useState(null);
  const [product, setProduct] = useState(null);
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  useEffect(() => {
    if (userId) fetchRecipient();
    if (postId) fetchPost();
    if (productId) fetchProduct();
    // Restore draft message if user was just redirected to auth and came back
    try {
      const draft = sessionStorage.getItem("draft_message");
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.userId === userId && parsed.postId === postId && parsed.productId === productId) {
          setMessage(parsed.message || "");
          sessionStorage.removeItem("draft_message");
        }
      }
    } catch (_) {}
  }, [userId, postId, productId]);

  const fetchRecipient = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setRecipient(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/conversations`, {
        recipient_id: userId,
        post_id: postId || null,
        product_id: productId || null,
        initial_message: message,
      });
      navigate(`/messages/${response.data.conversation_id}`);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 401 || detail === "Non authentifié") {
        // Session expired — try to refresh silently before giving up
        try {
          await axios.get(`${API}/auth/me`);
          // Auth still valid server-side → retry once
          try {
            const retry = await axios.post(`${API}/conversations`, {
              recipient_id: userId,
              post_id: postId || null,
              product_id: productId || null,
              initial_message: message,
            });
            navigate(`/messages/${retry.data.conversation_id}`);
            return;
          } catch (e2) {
            toast.error(lang === "fr" ? "Erreur lors de l'envoi. Veuillez réessayer." : "Error sending message. Please retry.");
          }
        } catch (_) {
          toast.error(lang === "fr" ? "Session expirée. Veuillez vous reconnecter." : "Session expired. Please log in again.");
          // Preserve the drafted message in sessionStorage so user doesn't lose it
          try {
            sessionStorage.setItem("draft_message", JSON.stringify({ userId, postId, productId, message }));
          } catch (_) {}
          navigate("/auth");
        }
      } else {
        toast.error(lang === "fr" ? "Erreur lors de l'envoi" : "Error sending message");
      }
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <Card className="bg-white rounded-2xl">
            <CardHeader>
              <CardTitle className="font-['Outfit']">{t("new_message_title")}</CardTitle>
            </CardHeader>
            <CardContent>
              {recipient && (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl mb-6">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={recipient.picture} />
                    <AvatarFallback>{recipient.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-slate-900">{recipient.name}</p>
                    {post && <p className="text-sm text-sky-600">📌 {post.title}</p>}
                    {product && <p className="text-sm text-sky-600">🛒 {product.title} - {formatPrice(product.price)}</p>}
                  </div>
                </div>
              )}

              <form onSubmit={handleSend} className="space-y-4">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("new_message_placeholder")}
                  rows={5}
                  required
                  className="rounded-xl"
                  data-testid="new-message-input"
                />
                <Button type="submit" disabled={sending} className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 font-bold" data-testid="send-new-message">
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : t("send")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

// ==================== PROFILE PAGE ====================
const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    ville: "",
    telephone: "",
  });
  const [saving, setSaving] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postFormData, setPostFormData] = useState({
    title: "",
    description: "",
    price: "",
    ville: "",
  });
  const [savingPost, setSavingPost] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Push Notifications
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, permission: pushPermission, subscribe: subscribePush, unsubscribe: unsubscribePush, testNotification } = usePushNotifications();
  const [pushLoading, setPushLoading] = useState(false);

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
        toast.success(lang === "fr" ? "Notifications désactivées" : "Notifications disabled");
      } else {
        const success = await subscribePush();
        if (success) {
          toast.success(lang === "fr" ? "Notifications activées" : "Notifications enabled");
        } else if (pushPermission === 'denied') {
          toast.error(lang === "fr" ? "Les notifications sont bloquées dans votre navigateur" : "Notifications are blocked in your browser");
        }
      }
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await testNotification();
      toast.success(lang === "fr" ? "Notification de test envoyée" : "Test notification sent");
    } catch (error) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  // Email notifications preferences
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/email-preferences`);
        setEmailEnabled(res.data.email_notifications_enabled);
      } catch (_) {}
    })();
  }, [user]);

  const handleEmailToggle = async () => {
    setEmailLoading(true);
    const next = !emailEnabled;
    try {
      await axios.put(`${API}/email-preferences`, { enabled: next });
      setEmailEnabled(next);
      toast.success(
        next
          ? (lang === "fr" ? "Notifications email activées" : "Email notifications enabled")
          : (lang === "fr" ? "Notifications email désactivées" : "Email notifications disabled")
      );
    } catch (_) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        ville: user.ville || "",
        telephone: user.telephone || "",
      });
      fetchReviews();
      fetchMyPosts();
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.user_id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchMyPosts = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.user_id}/posts`);
      setMyPosts(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, formData);
      await checkAuth();
      toast.success(lang === "fr" ? "Profil mis à jour" : "Profile updated");
      setEditing(false);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/users/me/picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await checkAuth();
      toast.success(lang === "fr" ? "Photo mise à jour" : "Photo updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setUploading(false);
    }
  };

  const getUserPictureUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith("letzservices/")) {
      return `${API}/files/${picture}`;
    }
    return picture;
  };

  const openEditPost = (post) => {
    setPostFormData({
      title: post.title || "",
      description: post.description || "",
      price: post.price || "",
      ville: post.ville || "",
    });
    setEditingPost(post);
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    setSavingPost(true);
    try {
      await axios.put(`${API}/posts/${editingPost.post_id}`, {
        ...editingPost,
        ...postFormData,
      });
      toast.success(lang === "fr" ? "Annonce mise à jour" : "Post updated");
      setEditingPost(null);
      fetchMyPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setSavingPost(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cette annonce ?" : "Delete this post?")) return;
    setDeletingPost(postId);
    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success(lang === "fr" ? "Annonce supprimée" : "Post deleted");
      fetchMyPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setDeletingPost(null);
    }
  };

  if (!user) return null;

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl overflow-hidden">
            <div className="h-32 bg-gradient-to-r from-sky-600 to-sky-700" />
            <CardContent className="relative pt-0 px-8 pb-8">
              <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
                <div className="relative">
                  <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                    <AvatarImage src={getUserPictureUrl(user.picture)} />
                    <AvatarFallback className="text-4xl">{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute bottom-0 right-0 w-10 h-10 bg-sky-600 hover:bg-sky-700 text-white rounded-full flex items-center justify-center shadow-lg"
                    data-testid="upload-photo-btn"
                  >
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                  </button>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{user.name}</h1>
                  <p className="text-slate-500">{user.email}</p>
                  {user.average_rating > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold">{user.average_rating}</span>
                      <span className="text-slate-500">({user.total_reviews} {lang === "fr" ? "avis" : "reviews"})</span>
                    </div>
                  )}
                </div>
                <Button onClick={() => setEditing(!editing)} variant="outline" className="rounded-xl">
                  {editing ? t("profile_cancel") : t("profile_edit")}
                </Button>
              </div>

              {editing ? (
                <form onSubmit={handleSave} className="mt-8 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("profile_name")}</label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("profile_bio")}</label>
                    <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder={t("profile_bio_placeholder")} rows={3} className="rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t("profile_ville")}</label>
                      <Input value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} placeholder={t("profile_ville_placeholder")} className="rounded-xl" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t("profile_phone")}</label>
                      <Input value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} placeholder={t("profile_phone_placeholder")} className="rounded-xl" />
                    </div>
                  </div>
                  <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile_save")}
                  </Button>
                </form>
              ) : (
                <div className="mt-8 space-y-4">
                  {user.bio && <p className="text-slate-600">{user.bio}</p>}
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    {user.ville && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {user.ville}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {t("profile_member_since")} {new Date(user.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { month: "long", year: "numeric" })}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Push Notifications Settings */}
          {pushSupported && (
            <Card className="bg-white rounded-2xl mt-6">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {lang === "fr" ? "Notifications Push" : "Push Notifications"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {lang === "fr" ? "Recevoir des notifications" : "Receive notifications"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {lang === "fr" 
                        ? "Soyez alerté des nouveaux messages même quand le site est fermé" 
                        : "Get alerted about new messages even when the site is closed"}
                    </p>
                    {pushPermission === 'denied' && (
                      <p className="text-sm text-red-500 mt-1">
                        {lang === "fr" 
                          ? "Les notifications sont bloquées. Modifiez les paramètres de votre navigateur." 
                          : "Notifications are blocked. Change your browser settings."}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {pushSubscribed && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleTestNotification}
                        className="rounded-xl"
                        data-testid="test-notification-btn"
                      >
                        {lang === "fr" ? "Tester" : "Test"}
                      </Button>
                    )}
                    <Button
                      onClick={handlePushToggle}
                      disabled={pushLoading || pushPermission === 'denied'}
                      className={`rounded-xl ${pushSubscribed ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}
                      data-testid="toggle-notifications-btn"
                    >
                      {pushLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : pushSubscribed ? (
                        lang === "fr" ? "Désactiver" : "Disable"
                      ) : (
                        lang === "fr" ? "Activer" : "Enable"
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email Notifications Settings */}
          <Card className="bg-white rounded-2xl mt-6" data-testid="email-notifications-card">
            <CardHeader>
              <CardTitle className="font-['Outfit'] flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {lang === "fr" ? "Notifications par email" : "Email notifications"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">
                    {lang === "fr" ? "Recevoir les emails LëtzServices" : "Receive LëtzServices emails"}
                  </p>
                  <p className="text-sm text-slate-500">
                    {lang === "fr"
                      ? "Soyez averti par email lorsqu'une nouvelle annonce ou un nouveau produit est publié."
                      : "Get notified by email when a new listing or product is published."}
                  </p>
                </div>
                <Button
                  onClick={handleEmailToggle}
                  disabled={emailLoading}
                  className={`rounded-xl shrink-0 ${emailEnabled ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}
                  data-testid="toggle-email-notifications-btn"
                >
                  {emailLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : emailEnabled ? (
                    lang === "fr" ? "Désactiver" : "Disable"
                  ) : (
                    lang === "fr" ? "Activer" : "Enable"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {reviews.length > 0 && (
            <Card className="bg-white rounded-2xl mt-6">
              <CardHeader>
                <CardTitle className="font-['Outfit']">{t("reviews_received_title")} ({reviews.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={review.reviewer_picture} />
                          <AvatarFallback>{review.reviewer_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium">{review.reviewer_name}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`} />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-slate-400">{new Date(review.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                      <p className="text-slate-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* My Posts Section */}
          <Card className="bg-white rounded-2xl mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Outfit']">
                {lang === "fr" ? "Mes annonces" : "My posts"} ({myPosts.length})
              </CardTitle>
              <Button 
                onClick={() => navigate("/annonces/publier")} 
                size="sm" 
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-1" />
                {lang === "fr" ? "Nouvelle" : "New"}
              </Button>
            </CardHeader>
            <CardContent>
              {myPosts.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  {lang === "fr" ? "Aucune annonce pour le moment" : "No posts yet"}
                </p>
              ) : (
                <div className="space-y-4">
                  {myPosts.map((post) => (
                    <div 
                      key={post.post_id} 
                      className="p-4 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-start justify-between">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={post.post_type === "demande" ? "bg-blue-100 text-blue-700" : "bg-sky-100 text-sky-700"}>
                              {post.post_type === "demande" ? t("posts_type_recherche") : t("posts_type_propose")}
                            </Badge>
                            {post.status === "completed" && (
                              <Badge className="bg-green-100 text-green-700">
                                {lang === "fr" ? "Terminé" : "Completed"}
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium text-slate-900 hover:text-sky-600">{post.title}</p>
                          <p className="text-sm text-slate-500 mt-1">{post.ville}</p>
                          {post.price && <p className="text-sky-600 font-semibold mt-2">{formatPrice(post.price)}</p>}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => openEditPost(post)}
                            className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                            title={lang === "fr" ? "Modifier" : "Edit"}
                            data-testid={`edit-post-${post.post_id}`}
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePost(post.post_id)}
                            disabled={deletingPost === post.post_id}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title={lang === "fr" ? "Supprimer" : "Delete"}
                            data-testid={`delete-post-${post.post_id}`}
                          >
                            {deletingPost === post.post_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Edit Post Modal */}
      {editingPost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 font-['Outfit']">
              {lang === "fr" ? "Modifier l'annonce" : "Edit post"}
            </h3>
            <form onSubmit={handleSavePost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {lang === "fr" ? "Titre" : "Title"}
                </label>
                <Input
                  value={postFormData.title}
                  onChange={(e) => setPostFormData({ ...postFormData, title: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <Textarea
                  value={postFormData.description}
                  onChange={(e) => setPostFormData({ ...postFormData, description: e.target.value })}
                  rows={4}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {lang === "fr" ? "Ville" : "City"}
                  </label>
                  <Input
                    value={postFormData.ville}
                    onChange={(e) => setPostFormData({ ...postFormData, ville: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {lang === "fr" ? "Prix (optionnel)" : "Price (optional)"}
                  </label>
                  <Input
                    value={postFormData.price}
                    onChange={(e) => setPostFormData({ ...postFormData, price: e.target.value })}
                    placeholder="ex: 50€/h"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="submit"
                  disabled={savingPost}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                >
                  {savingPost ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    lang === "fr" ? "Enregistrer" : "Save"
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={() => setEditingPost(null)}
                  variant="outline"
                  className="rounded-xl"
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
};

// ==================== PUBLIC PROFILE PAGE ====================
const PublicProfilePage = () => {
  const { userId } = useParams();
  const { t, lang } = useLanguage();
  const { user: currentUser, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingReview, setDeletingReview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchPosts();
    fetchProducts();
    fetchReviews();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error(lang === "fr" ? "Profil non trouvé" : "Profile not found");
      navigate("/annonces");
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cet avis ?" : "Delete this review?")) return;
    setDeletingReview(reviewId);
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success(lang === "fr" ? "Avis supprimé" : "Review deleted");
      fetchReviews();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setDeletingReview(null);
    }
  };

  const handleContact = () => {
    if (!currentUser) {
      toast.info(lang === "fr" ? "Connectez-vous pour contacter ce membre" : "Sign in to contact this member");
      login();
      return;
    }
    navigate(`/messages/new?userId=${userId}`);
  };

  const getUserPictureUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith("letzservices/")) {
      return `${API}/files/${picture}`;
    }
    return picture;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </>
    );
  }

  if (!profile) return null;

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <Card className="bg-white rounded-2xl overflow-hidden">
            <div className="h-32 bg-gradient-to-r from-sky-600 to-sky-700" />
            <CardContent className="relative pt-0 px-8 pb-8">
              <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
                <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                  <AvatarImage src={getUserPictureUrl(profile.picture)} />
                  <AvatarFallback className="text-4xl">{profile.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{profile.name}</h1>
                  {profile.ville && (
                    <p className="text-slate-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      {profile.ville}
                    </p>
                  )}
                  {profile.average_rating > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold">{profile.average_rating}</span>
                      <span className="text-slate-500">({profile.total_reviews} {lang === "fr" ? "avis" : "reviews"})</span>
                    </div>
                  )}
                </div>
                {currentUser?.user_id !== userId && (
                  <Button 
                    onClick={handleContact} 
                    className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                    data-testid="contact-profile-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t("profile_contact")}
                  </Button>
                )}
              </div>

              {profile.bio && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-slate-600">{profile.bio}</p>
                </div>
              )}

              <div className="mt-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t("profile_member_since")} {new Date(profile.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { month: "long", year: "numeric" })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* User's Posts */}
          {posts.length > 0 && (
            <Card className="bg-white rounded-2xl mt-6">
              <CardHeader>
                <CardTitle className="font-['Outfit']">{t("profile_view_posts")} ({posts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div 
                      key={post.post_id} 
                      className="p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={post.post_type === "demande" ? "bg-blue-100 text-blue-700" : "bg-sky-100 text-sky-700"}>
                          {post.post_type === "demande" ? t("posts_type_recherche") : t("posts_type_propose")}
                        </Badge>
                      </div>
                      <p className="font-medium text-slate-900">{post.title}</p>
                      <p className="text-sm text-slate-500 mt-1">{post.ville}</p>
                      {post.price && <p className="text-sky-600 font-semibold mt-2">{formatPrice(post.price)}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Products */}
          {products.length > 0 && (
            <Card className="bg-white rounded-2xl mt-6">
              <CardHeader>
                <CardTitle className="font-['Outfit']">{lang === "fr" ? "Produits en vente" : "Products for sale"} ({products.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {products.map((product) => (
                    <div 
                      key={product.product_id} 
                      className="p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/marketplace/${product.slug || product.product_id}`)}
                    >
                      {product.images?.[0] && (
                        <img 
                          src={product.images[0].startsWith("letzservices/") ? `${API}/files/${product.images[0]}` : product.images[0]}
                          alt={product.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={product.status === "available" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                          {product.status === "available" ? (lang === "fr" ? "Disponible" : "Available") : (lang === "fr" ? "Réservé" : "Reserved")}
                        </Badge>
                        {product.condition && (
                          <Badge variant="outline" className="text-xs">{product.condition}</Badge>
                        )}
                      </div>
                      <p className="font-medium text-slate-900">{product.title}</p>
                      <p className="text-sm text-slate-500 mt-1">{product.ville}</p>
                      <p className="text-sky-600 font-bold mt-2">{formatPrice(product.price)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <Card className="bg-white rounded-2xl mt-6">
              <CardHeader>
                <CardTitle className="font-['Outfit']">{t("profile_reviews_received")} ({reviews.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar 
                          className="w-10 h-10 cursor-pointer"
                          onClick={() => navigate(`/user/${review.reviewer_id}`)}
                        >
                          <AvatarImage src={getUserPictureUrl(review.reviewer_picture)} />
                          <AvatarFallback>{review.reviewer_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p 
                            className="font-medium cursor-pointer hover:text-sky-600"
                            onClick={() => navigate(`/user/${review.reviewer_id}`)}
                          >
                            {review.reviewer_name}
                          </p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`} />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-400">
                            {new Date(review.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB")}
                          </span>
                          {currentUser?.user_id === review.reviewer_id && (
                            <button
                              onClick={() => handleDeleteReview(review.review_id)}
                              disabled={deletingReview === review.review_id}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title={lang === "fr" ? "Supprimer mon avis" : "Delete my review"}
                              data-testid={`delete-review-${review.review_id}`}
                            >
                              {deletingReview === review.review_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-slate-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

// ==================== MARKETPLACE PAGE (LëtzVerkafen) ====================
const MarketplacePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchVille, setSearchVille] = useState("");
  const { t, lang } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, [filter]);

  const fetchProducts = async () => {
    try {
      const status = filter === "sold" ? "sold" : "available";
      const response = await axios.get(`${API}/products`, { params: { status } });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter((p) =>
    searchVille ? p.ville?.toLowerCase().includes(searchVille.toLowerCase()) : true
  );

  const getConditionLabel = (condition) => {
    const labels = {
      neuf: t("marketplace_condition_neuf"),
      comme_neuf: t("marketplace_condition_comme_neuf"),
      bon_etat: t("marketplace_condition_bon_etat"),
      etat_correct: t("marketplace_condition_etat_correct"),
    };
    return labels[condition] || condition;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API}/files/${imagePath}`;
  };

  return (
    <>
      <SEO 
        title={lang === "fr" ? "LëtzVerkafen - Marketplace Luxembourg" : "LëtzVerkafen - Luxembourg Marketplace"}
        description={lang === "fr" 
          ? "Achetez et vendez des objets d'occasion au Luxembourg. Vêtements, électronique, meubles et plus. 100% local."
          : "Buy and sell second-hand items in Luxembourg. Clothes, electronics, furniture and more. 100% local."}
        url="/marketplace"
      />
      <BreadcrumbSchema items={[
        { name: lang === "fr" ? "Accueil" : "Home", url: "/" },
        { name: "LëtzVerkafen", url: "/marketplace" }
      ]} />
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">{t("marketplace_title")}</h1>
              <p className="text-slate-600 mt-1">{t("marketplace_subtitle")}</p>
            </div>
            <Button
              onClick={() => (user ? navigate("/marketplace/new") : login())}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
              data-testid="create-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("marketplace_create")}
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Tabs value={filter} onValueChange={setFilter} className="w-full sm:w-auto">
              <TabsList className="bg-white rounded-xl">
                <TabsTrigger value="all" className="rounded-lg">{t("marketplace_filter_all")}</TabsTrigger>
                <TabsTrigger value="available" className="rounded-lg">{t("marketplace_filter_available")}</TabsTrigger>
                <TabsTrigger value="sold" className="rounded-lg">{t("marketplace_filter_sold")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t("marketplace_search")}
                value={searchVille}
                onChange={(e) => setSearchVille(e.target.value)}
                className="pl-10 rounded-xl bg-white"
                data-testid="marketplace-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <Card className="bg-white rounded-2xl p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t("marketplace_no_results")}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, idx) => (
                <Card
                  key={product.product_id}
                  className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all group"
                  onClick={() => navigate(`/marketplace/${product.slug || product.product_id}`)}
                  data-testid={`product-${product.product_id}`}
                >
                  <div className="aspect-square bg-slate-100 relative overflow-hidden">
                    {product.images?.[0] ? (
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.title}
                        loading={idx < 4 ? "eager" : "lazy"}
                        fetchpriority={idx < 4 ? "high" : "auto"}
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {product.status === "sold" && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge className="bg-red-500 text-white text-lg px-4 py-2">{t("marketplace_status_sold")}</Badge>
                      </div>
                    )}
                    {product.negotiable && product.status !== "sold" && (
                      <Badge className="absolute top-2 right-2 bg-green-500 text-white text-xs">
                        {t("marketplace_negotiable")}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="font-bold text-lg text-sky-600 mb-1">{formatPrice(product.price)}</p>
                    <h3 className="font-semibold text-slate-900 truncate">{product.title}</h3>
                    <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                      <Badge variant="outline" className="text-xs">{getConditionLabel(product.condition)}</Badge>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {product.ville}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                      <span className="text-sm text-slate-600 truncate">{product.user_name}</span>
                      {product.user_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-yellow-600">
                          <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                          {product.user_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

// ==================== CREATE PRODUCT PAGE ====================
const CreateProductPage = () => {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    condition: "bon_etat",
    price: "",
    negotiable: false,
    ville: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // All categories for autocomplete
  const allCategories = lang === "fr" ? [
    "Électronique", "Téléphones", "Ordinateurs", "Tablettes", "TV & Audio",
    "Meubles", "Canapés", "Tables", "Chaises", "Lits", "Armoires",
    "Vêtements", "Homme", "Femme", "Enfant", "Chaussures", "Accessoires",
    "Véhicules", "Voitures", "Motos", "Vélos", "Pièces auto",
    "Sports & Loisirs", "Fitness", "Football", "Tennis", "Randonnée",
    "Maison & Jardin", "Décoration", "Jardinage", "Bricolage", "Électroménager",
    "Enfants", "Jouets", "Puériculture", "Vêtements enfant",
    "Livres & Médias", "Livres", "Jeux vidéo", "DVD & Blu-ray", "Musique",
    "Immobilier", "Appartement", "Maison", "Terrain", "Colocation",
    "Autre"
  ] : [
    "Electronics", "Phones", "Computers", "Tablets", "TV & Audio",
    "Furniture", "Sofas", "Tables", "Chairs", "Beds", "Wardrobes",
    "Clothing", "Men", "Women", "Kids", "Shoes", "Accessories",
    "Vehicles", "Cars", "Motorcycles", "Bikes", "Auto parts",
    "Sports & Leisure", "Fitness", "Football", "Tennis", "Hiking",
    "Home & Garden", "Decoration", "Gardening", "DIY", "Appliances",
    "Kids", "Toys", "Baby items", "Kids clothing",
    "Books & Media", "Books", "Video games", "DVD & Blu-ray", "Music",
    "Real Estate", "Apartment", "House", "Land", "Roommate",
    "Other"
  ];

  // Popular cities for quick selection
  const popularCities = [
    "Luxembourg-Ville", "Esch-sur-Alzette", "Differdange", "Dudelange",
    "Ettelbruck", "Diekirch", "Wiltz", "Echternach", "Remich", "Vianden"
  ];

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
    if (value.length >= 1) {
      const filtered = allCategories.filter(cat => 
        cat.toLowerCase().includes(value.toLowerCase())
      );
      setCategorySuggestions(filtered.slice(0, 8));
      setShowCategorySuggestions(filtered.length > 0);
    } else {
      setCategorySuggestions([]);
      setShowCategorySuggestions(false);
    }
  };

  const selectCategory = (category) => {
    setFormData({ ...formData, category });
    setShowCategorySuggestions(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 10 - selectedFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);
    
    if (filesToAdd.length > 0) {
      setSelectedFiles(prev => [...prev, ...filesToAdd]);
      const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviews]);
    }
    
    if (files.length > remainingSlots) {
      toast.info(lang === "fr" ? "Maximum 10 photos" : "Maximum 10 photos");
    }
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleCityChange = (value) => {
    setFormData({ ...formData, ville: value });
    if (value.length >= 2) {
      const suggestions = searchCities(value);
      setCitySuggestions(suggestions);
      setShowCitySuggestions(suggestions.length > 0);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const selectCity = (city) => {
    const cityName = typeof city === 'string' ? city : formatCity(city.city, city.postalCode);
    setFormData({ ...formData, ville: cityName });
    setShowCitySuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error(t("marketplace_photo_required"));
      return;
    }
    
    // Validate description length
    if (formData.description.length < 10) {
      toast.error(lang === "fr" ? "La description doit faire au moins 10 caractères" : "Description must be at least 10 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      // Create product first
      const response = await axios.post(`${API}/products`, formData);
      const productId = response.data.product_id;
      
      // Upload all images
      for (const file of selectedFiles) {
        const imageFormData = new FormData();
        imageFormData.append("file", file);
        await axios.post(`${API}/products/${productId}/images`, imageFormData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      toast.success(lang === "fr" ? "Produit publié !" : "Product posted!");
      navigate("/marketplace");
    } catch (error) {
      console.error("Product creation error:", error.response?.data);
      const detail = error.response?.data?.detail;
      if (detail === "Non authentifié") {
        toast.error(lang === "fr" ? "Veuillez vous connecter pour publier" : "Please login to post");
        navigate("/auth");
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      } else {
        toast.error(lang === "fr" ? "Erreur lors de la publication" : "Error posting product");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const conditions = [
    { value: "neuf", label: t("marketplace_condition_neuf") },
    { value: "comme_neuf", label: t("marketplace_condition_comme_neuf") },
    { value: "bon_etat", label: t("marketplace_condition_bon_etat") },
    { value: "etat_correct", label: t("marketplace_condition_etat_correct") },
  ];

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <Card className="bg-white rounded-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold font-['Outfit']">{t("create_product_title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Multi-Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("create_product_photos")} ({selectedFiles.length}/10)
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {previewUrls.map((url, idx) => (
                      <div key={idx} className="relative aspect-square">
                        <img src={url} alt="" className="w-full h-full object-cover rounded-xl" />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {selectedFiles.length < 10 && (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-sky-500 transition-colors"
                      >
                        <Camera className="w-8 h-8 text-slate-400" />
                        <span className="text-xs text-slate-400 mt-1">+</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <p className="text-xs text-slate-500 mt-2">{t("create_product_photos_hint")}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_product_name")}</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t("create_product_name_placeholder")}
                    required
                    className="rounded-xl"
                    data-testid="product-title"
                  />
                </div>

                {/* Category with autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_product_category")}</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    onFocus={() => formData.category.length >= 1 && setShowCategorySuggestions(categorySuggestions.length > 0)}
                    onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                    placeholder={t("create_product_category_placeholder")}
                    required
                    className="rounded-xl"
                    data-testid="product-category"
                  />
                  {showCategorySuggestions && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {categorySuggestions.map((cat, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectCategory(cat)}
                          className="w-full text-left px-4 py-2 hover:bg-sky-50 text-slate-700"
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_product_condition")}</label>
                  <div className="grid grid-cols-2 gap-3">
                    {conditions.map((cond) => (
                      <label
                        key={cond.value}
                        className={`p-3 border rounded-xl cursor-pointer transition-all text-center ${
                          formData.condition === cond.value
                            ? "border-sky-600 bg-sky-50 text-sky-700"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="condition"
                          value={cond.value}
                          checked={formData.condition === cond.value}
                          onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                          className="sr-only"
                        />
                        {cond.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_product_price")}</label>
                    <div className="relative">
                      <Input
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder={t("create_product_price_placeholder")}
                        required
                        className="rounded-xl pr-12"
                        data-testid="product-price"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">€</span>
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_ville_label")}</label>
                    <Input
                      value={formData.ville}
                      onChange={(e) => handleCityChange(e.target.value)}
                      onFocus={() => {
                        if (formData.ville.length >= 2) {
                          setShowCitySuggestions(citySuggestions.length > 0);
                        } else {
                          // Show popular cities when empty
                          setCitySuggestions(popularCities.map(c => ({ city: c, postalCode: "" })));
                          setShowCitySuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                      placeholder={t("create_ville_placeholder")}
                      required
                      className="rounded-xl"
                      data-testid="product-ville"
                    />
                    {showCitySuggestions && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {citySuggestions.map((city, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => selectCity(city)}
                            className="w-full text-left px-4 py-2 hover:bg-sky-50 flex justify-between items-center"
                          >
                            <span>{typeof city === 'string' ? city : city.city}</span>
                            {city.postalCode && <span className="text-sm text-slate-500">{city.postalCode}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.negotiable}
                    onChange={(e) => setFormData({ ...formData, negotiable: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-slate-700">{t("create_product_negotiable")}</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("create_product_description")}</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t("create_product_description_placeholder")}
                    rows={5}
                    required
                    className="rounded-xl"
                    data-testid="product-description"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-6 font-bold"
                  data-testid="submit-product"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t("create_product_submit")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

// ==================== PRODUCT DETAIL PAGE ====================
const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { t, lang } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    // Check favorite only after product is loaded (to use real product_id)
    if (user && product?.product_id) {
      checkFavorite();
    }
  }, [user, product?.product_id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      toast.error(lang === "fr" ? "Produit non trouvé" : "Product not found");
      navigate("/marketplace");
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      // Use product.product_id to ensure we check with the real ID, not the slug
      const idToCheck = product?.product_id || productId;
      const response = await axios.get(`${API}/favorites/check/product/${idToCheck}`);
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error("Error checking favorite:", error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.info(lang === "fr" ? "Connectez-vous pour sauvegarder" : "Sign in to save");
      login();
      return;
    }
    // Use the real product_id from the loaded product, not the URL param (which might be a slug)
    const realProductId = product?.product_id || productId;
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/product/${realProductId}`);
        setIsFavorite(false);
        toast.success(lang === "fr" ? "Retiré des favoris" : "Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, { product_id: realProductId });
        setIsFavorite(true);
        toast.success(lang === "fr" ? "Ajouté aux favoris" : "Added to favorites");
      }
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleContact = () => {
    if (!user) {
      login();
      return;
    }
    navigate(`/messages/new?userId=${product.user_id}&productId=${product.product_id}`);
  };

  const openEditModal = () => {
    // Extract numeric price (remove € symbol if present)
    let priceValue = product.price || "";
    if (typeof priceValue === 'string') {
      priceValue = priceValue.replace(/[€\s]/g, '').trim();
    }
    
    setEditForm({
      title: product.title || "",
      description: product.description || "",
      price: priceValue,
      category: product.category || "",
      ville: product.ville || "",
      condition: product.condition || "Bon état"
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await axios.put(`${API}/products/${product.product_id}`, editForm);
      toast.success(lang === "fr" ? "Produit modifié !" : "Product updated!");
      setShowEditModal(false);
      fetchProduct();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setEditLoading(false);
    }
  };

  const openSoldModal = async () => {
    // Fetch conversations for this product to get potential buyers
    try {
      const response = await axios.get(`${API}/conversations`);
      const productConversations = response.data.filter(
        (conv) => conv.product_id === productId
      );
      const potentialBuyers = productConversations.map((conv) => ({
        user_id: conv.other_user.user_id,
        name: conv.other_user.name,
        picture: conv.other_user.picture,
      }));
      setBuyers(potentialBuyers);
      setShowSoldModal(true);
    } catch (error) {
      console.error("Error fetching buyers:", error);
      // If no conversations, just mark as sold without buyer
      handleMarkSold(null);
    }
  };

  const handleMarkSold = async (buyerId) => {
    try {
      await axios.patch(`${API}/products/${productId}/status`, { 
        status: "sold",
        buyer_id: buyerId 
      });
      setShowSoldModal(false);
      toast.success(
        lang === "fr" 
          ? buyerId ? "Marqué comme vendu ! L'acheteur pourra vous noter." : "Marqué comme vendu"
          : buyerId ? "Marked as sold! The buyer can now rate you." : "Marked as sold"
      );
      fetchProduct();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const getConditionLabel = (condition) => {
    const labels = {
      neuf: t("marketplace_condition_neuf"),
      comme_neuf: t("marketplace_condition_comme_neuf"),
      bon_etat: t("marketplace_condition_bon_etat"),
      etat_correct: t("marketplace_condition_etat_correct"),
    };
    return labels[condition] || condition;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API}/files/${imagePath}`;
  };

  const getUserPictureUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith("letzservices/")) return `${API}/files/${picture}`;
    return picture;
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="pt-24 pb-16 min-h-screen bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </main>
      </>
    );
  }

  if (!product) return null;

  return (
    <>
      <SEO 
        title={product.title}
        description={product.description?.substring(0, 160)}
        image={product.images?.[0] ? getImageUrl(product.images[0]) : null}
        url={`/marketplace/${product.slug || product.product_id}`}
        type="product"
      />
      <ProductSchema product={product} />
      <BreadcrumbSchema items={[
        { name: lang === "fr" ? "Accueil" : "Home", url: "/" },
        { name: "LëtzVerkafen", url: "/marketplace" },
        { name: product.title, url: `/marketplace/${product.slug || product.product_id}` }
      ]} />
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Images */}
            <div className="space-y-4">
              <div 
                className="aspect-square bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => product.images?.[0] && setLightboxIndex(0)}
                data-testid="product-main-image"
              >
                {product.images?.[0] ? (
                  <img
                    src={getImageUrl(product.images[0])}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-100">
                    <Package className="w-24 h-24 text-slate-300" />
                  </div>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.images.slice(1).map((img, idx) => (
                    <div 
                      key={idx} 
                      className="aspect-square bg-white rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxIndex(idx + 1)}
                      data-testid={`product-thumb-${idx}`}
                    >
                      <img src={getImageUrl(img)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-3xl font-bold text-sky-600">{formatPrice(product.price)}</p>
                    {product.negotiable && (
                      <Badge className="bg-green-100 text-green-700 mt-1">{t("marketplace_negotiable")}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleFavorite}
                      className={`p-2 rounded-full transition-colors ${isFavorite ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400 hover:text-red-500"}`}
                      data-testid="favorite-btn"
                    >
                      <Heart className={`w-6 h-6 ${isFavorite ? "fill-current" : ""}`} />
                    </button>
                    <Badge
                      variant={product.status === "sold" ? "destructive" : "default"}
                      className={product.status === "sold" ? "bg-red-500" : "bg-green-500"}
                    >
                      {product.status === "sold" ? t("marketplace_status_sold") : t("marketplace_status_available")}
                    </Badge>
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-4">{product.title}</h1>

                <div className="flex flex-wrap gap-3 mb-4">
                  <Badge variant="outline">{getConditionLabel(product.condition)}</Badge>
                  <Badge variant="outline">{product.category}</Badge>
                  <span className="flex items-center gap-1 text-slate-500">
                    <MapPin className="w-4 h-4" />
                    {product.ville}
                  </span>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold text-slate-900 mb-2">{t("create_product_description")}</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{product.description}</p>
                </div>
              </Card>

              {/* Seller Card */}
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <Avatar
                    className="w-14 h-14 cursor-pointer"
                    onClick={() => navigate(`/user/${product.user_id}`)}
                  >
                    <AvatarImage src={getUserPictureUrl(product.user_picture)} />
                    <AvatarFallback>{product.user_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p
                        className="font-semibold text-slate-900 cursor-pointer hover:text-sky-600"
                        onClick={() => navigate(`/user/${product.user_id}`)}
                      >
                        {product.user_name}
                      </p>
                      {product.user_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-sm text-yellow-600">
                          <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                          {product.user_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      {new Date(product.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB")}
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {user?.user_id === product.user_id ? (
                    product.status !== "sold" && (
                      <div className="space-y-2">
                        <Button
                          onClick={openEditModal}
                          variant="outline"
                          className="w-full rounded-xl"
                          data-testid="edit-product-btn"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          {lang === "fr" ? "Modifier" : "Edit"}
                        </Button>
                        <Button
                          onClick={openSoldModal}
                          className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl"
                          data-testid="mark-sold-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t("marketplace_mark_sold")}
                        </Button>
                      </div>
                    )
                  ) : (
                    product.status !== "sold" && (
                      <Button
                        onClick={handleContact}
                        className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                        data-testid="contact-seller-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {t("marketplace_contact_seller")}
                      </Button>
                    )
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Mark as Sold Modal */}
      {showSoldModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {lang === "fr" ? "Marquer comme vendu" : "Mark as sold"}
            </h3>
            
            {buyers.length > 0 ? (
              <>
                <p className="text-slate-600 mb-4">
                  {lang === "fr" 
                    ? "Sélectionnez l'acheteur pour qu'il puisse vous noter :" 
                    : "Select the buyer so they can rate you:"}
                </p>
                <div className="space-y-2 mb-4">
                  {buyers.map((buyer) => (
                    <button
                      key={buyer.user_id}
                      onClick={() => setSelectedBuyer(buyer.user_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        selectedBuyer === buyer.user_id 
                          ? "border-green-500 bg-green-50" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={getUserPictureUrl(buyer.picture)} />
                        <AvatarFallback>{buyer.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{buyer.name}</span>
                      {selectedBuyer === buyer.user_id && (
                        <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleMarkSold(selectedBuyer)}
                    disabled={!selectedBuyer}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    {lang === "fr" ? "Confirmer" : "Confirm"}
                  </Button>
                  <Button
                    onClick={() => handleMarkSold(null)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    {lang === "fr" ? "Sans acheteur" : "Without buyer"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-600 mb-4">
                  {lang === "fr" 
                    ? "Aucune conversation trouvée pour ce produit. Confirmer la vente ?" 
                    : "No conversations found for this product. Confirm the sale?"}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleMarkSold(null)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    {lang === "fr" ? "Confirmer" : "Confirm"}
                  </Button>
                  <Button
                    onClick={() => setShowSoldModal(false)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {lang === "fr" ? "Modifier le produit" : "Edit product"}
            </h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {lang === "fr" ? "Titre" : "Title"}
                </label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {lang === "fr" ? "Description" : "Description"}
                </label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={4}
                  className="rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "Prix (€)" : "Price (€)"}
                  </label>
                  <Input
                    type="number"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "État" : "Condition"}
                  </label>
                  <select
                    value={editForm.condition}
                    onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  >
                    <option value="Neuf">{lang === "fr" ? "Neuf" : "New"}</option>
                    <option value="Comme neuf">{lang === "fr" ? "Comme neuf" : "Like new"}</option>
                    <option value="Bon état">{lang === "fr" ? "Bon état" : "Good"}</option>
                    <option value="Usé">{lang === "fr" ? "Usé" : "Used"}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "Catégorie" : "Category"}
                  </label>
                  <Input
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {lang === "fr" ? "Ville" : "City"}
                  </label>
                  <Input
                    value={editForm.ville}
                    onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                >
                  {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "Enregistrer" : "Save")}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  variant="outline"
                  className="rounded-xl"
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Lightbox for images */}
      {lightboxIndex !== null && product.images && (
        <ImageLightbox 
          images={product.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          getImageUrl={getImageUrl}
        />
      )}
    </>
  );
};

// ==================== PENDING RATINGS PAGE ====================
const PendingRatingsPage = () => {
  const { t, lang } = useLanguage();
  const [pendingRatings, setPendingRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRating, setSelectedRating] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingRatings();
  }, []);

  const fetchPendingRatings = async () => {
    try {
      const response = await axios.get(`${API}/pending-ratings`);
      setPendingRatings(response.data);
    } catch (error) {
      console.error("Error fetching pending ratings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!selectedRating || ratingValue < 1) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/pending-ratings/${selectedRating.rating_id}/submit`, {
        rating: ratingValue,
        comment: comment
      });
      toast.success(lang === "fr" ? "Merci pour votre avis !" : "Thank you for your review!");
      setSelectedRating(null);
      setRatingValue(0);
      setComment("");
      fetchPendingRatings();
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async (ratingId) => {
    try {
      await axios.post(`${API}/pending-ratings/${ratingId}/skip`);
      fetchPendingRatings();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <h1 className="text-2xl font-bold mb-6">
            {lang === "fr" ? "Notations en attente" : "Pending Ratings"}
          </h1>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
            </div>
          ) : pendingRatings.length === 0 ? (
            <Card className="bg-white rounded-2xl p-8 text-center">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {lang === "fr" ? "Aucune notation en attente" : "No pending ratings"}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingRatings.map((rating) => (
                <Card key={rating.rating_id} className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Package className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{rating.product_title}</p>
                      <p className="text-sm text-slate-500">
                        {lang === "fr" ? "Vendu par" : "Sold by"} {rating.seller_name}
                      </p>
                    </div>
                  </div>

                  {selectedRating?.rating_id === rating.rating_id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {lang === "fr" ? "Votre note" : "Your rating"}
                        </label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              onClick={() => setRatingValue(star)}
                              className="focus:outline-none"
                            >
                              <Star
                                className={`w-8 h-8 ${
                                  star <= ratingValue
                                    ? "text-yellow-400 fill-yellow-400"
                                    : "text-slate-300"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {lang === "fr" ? "Commentaire (optionnel)" : "Comment (optional)"}
                        </label>
                        <Textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder={lang === "fr" ? "Partagez votre expérience..." : "Share your experience..."}
                          rows={3}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSubmitRating}
                          disabled={ratingValue < 1 || submitting}
                          className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "Envoyer" : "Submit")}
                        </Button>
                        <Button
                          onClick={() => setSelectedRating(null)}
                          variant="outline"
                          className="rounded-xl"
                        >
                          {lang === "fr" ? "Annuler" : "Cancel"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setSelectedRating(rating)}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        {lang === "fr" ? "Noter le vendeur" : "Rate seller"}
                      </Button>
                      <Button
                        onClick={() => handleSkip(rating.rating_id)}
                        variant="outline"
                        className="rounded-xl"
                      >
                        {lang === "fr" ? "Passer" : "Skip"}
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

// ==================== ADMIN DASHBOARD PAGE ====================
const AdminDashboardPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({ source_fr: "🇱🇺 Actu", source_en: "🇱🇺 News", text_fr: "", text_en: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, postsRes, productsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/posts`),
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setPosts(postsRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      
      // Fetch all reviews
      const reviewsRes = await axios.get(`${API}/reviews`).catch(() => ({ data: [] }));
      setReviews(reviewsRes.data || []);
      
      // Fetch news
      const newsRes = await axios.get(`${API}/admin/news`).catch(() => ({ data: [] }));
      setNews(newsRes.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(lang === "fr" ? "Accès non autorisé" : "Unauthorized access");
        navigate("/");
      } else {
        toast.error(error.response?.data?.detail || "Error loading admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cette annonce ?" : "Delete this post?")) return;
    setDeleting(postId);
    try {
      await axios.delete(`${API}/admin/posts/${postId}`);
      toast.success(lang === "fr" ? "Annonce supprimée" : "Post deleted");
      setPosts(posts.filter(p => p.post_id !== postId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(lang === "fr" ? "Supprimer ce produit ?" : "Delete this product?")) return;
    setDeleting(productId);
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success(lang === "fr" ? "Produit supprimé" : "Product deleted");
      setProducts(products.filter(p => p.product_id !== productId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cet avis ?" : "Delete this review?")) return;
    setDeleting(reviewId);
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success(lang === "fr" ? "Avis supprimé" : "Review deleted");
      setReviews(reviews.filter(r => r.review_id !== reviewId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: lang === "fr" ? "Vue d'ensemble" : "Overview", icon: LayoutDashboard },
    { id: "posts", label: lang === "fr" ? "Annonces" : "Posts", icon: FileText },
    { id: "products", label: lang === "fr" ? "Produits" : "Products", icon: Package },
    { id: "users", label: lang === "fr" ? "Utilisateurs" : "Users", icon: Users },
    { id: "reviews", label: lang === "fr" ? "Avis" : "Reviews", icon: Star },
    { id: "news", label: lang === "fr" ? "Actualités" : "News", icon: Newspaper },
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 font-['Outfit'] flex items-center gap-3">
              <Shield className="w-8 h-8 text-sky-600" />
              {lang === "fr" ? "Dashboard Admin" : "Admin Dashboard"}
            </h1>
            <p className="text-slate-500 mt-2">
              {lang === "fr" ? "Gérez votre plateforme LëtzServices" : "Manage your LëtzServices platform"}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-sky-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.stats?.total_users || 0}</p>
                    <p className="text-slate-500">{lang === "fr" ? "Utilisateurs" : "Users"}</p>
                  </div>
                </div>
              </Card>
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.stats?.total_posts || 0}</p>
                    <p className="text-slate-500">{lang === "fr" ? "Annonces" : "Posts"}</p>
                  </div>
                </div>
              </Card>
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Package className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.stats?.total_products || 0}</p>
                    <p className="text-slate-500">{lang === "fr" ? "Produits" : "Products"}</p>
                  </div>
                </div>
              </Card>
              <Card className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <MessageSquare className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.stats?.total_messages || 0}</p>
                    <p className="text-slate-500">{lang === "fr" ? "Messages" : "Messages"}</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Posts Tab */}
          {activeTab === "posts" && (
            <Card className="bg-white rounded-2xl">
              <CardHeader>
                <CardTitle>{lang === "fr" ? "Toutes les annonces" : "All Posts"} ({posts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {posts.map((post) => (
                    <div key={post.post_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{post.title}</p>
                        <p className="text-sm text-slate-500">
                          {post.user_name} • {post.ville} • {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/posts/${post.slug || post.post_id}`)}
                          className="rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePost(post.post_id)}
                          disabled={deleting === post.post_id}
                          className="rounded-lg"
                          data-testid={`admin-delete-post-${post.post_id}`}
                        >
                          {deleting === post.post_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {posts.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{lang === "fr" ? "Aucune annonce" : "No posts"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Products Tab */}
          {activeTab === "products" && (
            <Card className="bg-white rounded-2xl">
              <CardHeader>
                <CardTitle>{lang === "fr" ? "Tous les produits" : "All Products"} ({products.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {products.map((product) => (
                    <div key={product.product_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{product.title}</p>
                        <p className="text-sm text-slate-500">
                          {product.seller_name} • {formatPrice(product.price)} • {product.status}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/marketplace/${product.slug || product.product_id}`)}
                          className="rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteProduct(product.product_id)}
                          disabled={deleting === product.product_id}
                          className="rounded-lg"
                          data-testid={`admin-delete-product-${product.product_id}`}
                        >
                          {deleting === product.product_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{lang === "fr" ? "Aucun produit" : "No products"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <Card className="bg-white rounded-2xl">
              <CardHeader>
                <CardTitle>{lang === "fr" ? "Tous les utilisateurs" : "All Users"} ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback>{u.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={u.is_verified ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                          {u.is_verified ? "Vérifié" : "Non vérifié"}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/user/${u.user_id}`)}
                          className="rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <Card className="bg-white rounded-2xl">
              <CardHeader>
                <CardTitle>{lang === "fr" ? "Tous les avis" : "All Reviews"} ({reviews.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{review.reviewer_name}</span>
                            <span className="text-slate-400">→</span>
                            <span className="text-slate-600">{review.reviewed_user_id}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`} />
                            ))}
                          </div>
                          <p className="text-slate-600">{review.comment}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteReview(review.review_id)}
                          disabled={deleting === review.review_id}
                          className="rounded-lg"
                          data-testid={`admin-delete-review-${review.review_id}`}
                        >
                          {deleting === review.review_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {reviews.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{lang === "fr" ? "Aucun avis" : "No reviews"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* News Tab */}
          {activeTab === "news" && (
            <Card className="bg-white rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{lang === "fr" ? "Actualités Luxembourg" : "Luxembourg News"} ({news.length})</CardTitle>
                <Button 
                  onClick={() => { setEditingNews(null); setNewsForm({ source_fr: "🇱🇺 Actu", source_en: "🇱🇺 News", text_fr: "", text_en: "" }); setShowNewsModal(true); }}
                  className="bg-sky-600 hover:bg-sky-700 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {lang === "fr" ? "Ajouter" : "Add"}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {news.map((item) => (
                    <div key={item.news_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-sky-600 font-medium mb-1">{item.source_fr}</p>
                          <p className="text-slate-900 font-medium">{item.text_fr}</p>
                          <p className="text-sm text-slate-500 mt-1">{item.text_en}</p>
                          <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${item.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                            {item.active ? (lang === "fr" ? "Actif" : "Active") : (lang === "fr" ? "Inactif" : "Inactive")}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingNews(item); setNewsForm({ source_fr: item.source_fr, source_en: item.source_en, text_fr: item.text_fr, text_en: item.text_en }); setShowNewsModal(true); }}
                            className="rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              await axios.put(`${API}/admin/news/${item.news_id}`, { active: !item.active });
                              setNews(news.map(n => n.news_id === item.news_id ? { ...n, active: !n.active } : n));
                            }}
                            className="rounded-lg"
                          >
                            {item.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={async () => {
                              if (window.confirm(lang === "fr" ? "Supprimer cette actualité ?" : "Delete this news?")) {
                                await axios.delete(`${API}/admin/news/${item.news_id}`);
                                setNews(news.filter(n => n.news_id !== item.news_id));
                                toast.success(lang === "fr" ? "Actualité supprimée" : "News deleted");
                              }
                            }}
                            className="rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {news.length === 0 && (
                    <p className="text-center text-slate-500 py-8">{lang === "fr" ? "Aucune actualité" : "No news"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* News Modal */}
          {showNewsModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
                <h3 className="text-xl font-bold mb-4">
                  {editingNews ? (lang === "fr" ? "Modifier l'actualité" : "Edit News") : (lang === "fr" ? "Nouvelle actualité" : "New News")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Source (FR)</label>
                    <input
                      type="text"
                      value={newsForm.source_fr}
                      onChange={(e) => setNewsForm({ ...newsForm, source_fr: e.target.value })}
                      className="w-full border rounded-xl px-3 py-2"
                      placeholder="🇱🇺 Actu Lux"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Texte (FR)</label>
                    <textarea
                      value={newsForm.text_fr}
                      onChange={(e) => setNewsForm({ ...newsForm, text_fr: e.target.value })}
                      className="w-full border rounded-xl px-3 py-2"
                      rows={2}
                      placeholder="Les transports publics gratuits..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Source (EN)</label>
                    <input
                      type="text"
                      value={newsForm.source_en}
                      onChange={(e) => setNewsForm({ ...newsForm, source_en: e.target.value })}
                      className="w-full border rounded-xl px-3 py-2"
                      placeholder="🇱🇺 Lux News"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Text (EN)</label>
                    <textarea
                      value={newsForm.text_en}
                      onChange={(e) => setNewsForm({ ...newsForm, text_en: e.target.value })}
                      className="w-full border rounded-xl px-3 py-2"
                      rows={2}
                      placeholder="Free public transport..."
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => setShowNewsModal(false)}
                    variant="outline"
                    className="flex-1 rounded-xl"
                  >
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        if (editingNews) {
                          await axios.put(`${API}/admin/news/${editingNews.news_id}`, newsForm);
                          setNews(news.map(n => n.news_id === editingNews.news_id ? { ...n, ...newsForm } : n));
                          toast.success(lang === "fr" ? "Actualité modifiée" : "News updated");
                        } else {
                          const res = await axios.post(`${API}/admin/news`, newsForm);
                          setNews([{ news_id: res.data.news_id, ...newsForm, active: true, created_at: new Date().toISOString() }, ...news]);
                          toast.success(lang === "fr" ? "Actualité ajoutée" : "News added");
                        }
                        setShowNewsModal(false);
                      } catch (error) {
                        toast.error("Erreur");
                      }
                    }}
                    className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-xl"
                  >
                    {lang === "fr" ? "Enregistrer" : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

// Legal, About, and Unsubscribe pages migrated to /pages/ (imported at top)

// ==================== FAVORITES PAGE ====================
const FavoritesPage = () => {
  const { t, lang } = useLanguage();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (type, itemId) => {
    try {
      await axios.delete(`${API}/favorites/${type}/${itemId}`);
      toast.success(lang === "fr" ? "Retiré des favoris" : "Removed from favorites");
      fetchFavorites();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API}/files/${imagePath}`;
  };

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t("back")}
          </button>

          <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            {lang === "fr" ? "Mes favoris" : "My Favorites"}
          </h1>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
            </div>
          ) : favorites.length === 0 ? (
            <Card className="bg-white rounded-2xl p-8 text-center">
              <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {lang === "fr" ? "Vous n'avez pas encore de favoris" : "You don't have any favorites yet"}
              </p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate("/annonces")} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl">
                  {lang === "fr" ? "Voir les annonces" : "Browse listings"}
                </Button>
                <Button onClick={() => navigate("/marketplace")} variant="outline" className="rounded-xl">
                  {lang === "fr" ? "Voir le marketplace" : "Browse marketplace"}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {favorites.map((fav) => (
                <Card key={fav.favorite_id} className="bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    {fav.type === "product" && fav.item.photos?.[0] && (
                      <img 
                        src={getImageUrl(fav.item.photos[0])} 
                        alt={fav.item.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-40 object-cover"
                      />
                    )}
                    {fav.type === "post" && fav.item.photos?.[0] && (
                      <img 
                        src={getImageUrl(fav.item.photos[0])} 
                        alt={fav.item.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(fav.type, fav.type === "post" ? fav.post_id : fav.product_id);
                      }}
                      className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-red-50"
                    >
                      <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                    </button>
                  </div>
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => navigate(fav.type === "post" ? `/posts/${fav.post_slug || fav.post_id}` : `/marketplace/${fav.product_slug || fav.product_id}`)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={fav.type === "post" ? "bg-sky-100 text-sky-700" : "bg-green-100 text-green-700"}>
                        {fav.type === "post" 
                          ? (lang === "fr" ? "Annonce" : "Listing")
                          : (lang === "fr" ? "Produit" : "Product")
                        }
                      </Badge>
                      {fav.item.price && (
                        <span className="text-sky-600 font-bold">{formatPrice(fav.item.price)}</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{fav.item.title}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {fav.item.ville}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

// ==================== MAIN APP ROUTER ====================
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/annonces" element={<PostsPage />} />
      <Route path="/posts/:postId" element={<PostDetailPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/:productId" element={<ProductDetailPage />} />
      <Route
        path="/marketplace/new"
        element={
          <ProtectedRoute>
            <CreateProductPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts/new"
        element={
          <ProtectedRoute>
            <CreatePostPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/new"
        element={
          <ProtectedRoute>
            <NewMessagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <ProtectedRoute>
            <ConversationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route path="/user/:userId" element={<PublicProfilePage />} />
      <Route
        path="/pending-ratings"
        element={
          <ProtectedRoute>
            <PendingRatingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/legal/marketplace-rules" element={<MarketplaceRulesPage />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/terms-of-sale" element={<TermsOfSalePage />} />
      <Route path="/legal/terms-of-use" element={<TermsOfUsePage />} />
      <Route path="/unsubscribe/:userId" element={<UnsubscribePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ==================== BOTTOM NAVIGATION (Mobile) ====================
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const res = await axios.get(`${API}/notifications/unread-count`);
        setUnreadCount(res.data.unread_messages || 0);
      } catch (_) {}
    };
    fetchUnread();
    // Poll every 60s (was 30s) and only when tab is visible
    const interval = setInterval(fetchUnread, 60000);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  // Hide on auth pages
  if (location.pathname.startsWith("/auth")) return null;
  // Hide when user is not logged in — bottom nav is a "connected user" feature
  if (!user) return null;

  const triggerHaptic = () => {
    if (navigator.vibrate) navigator.vibrate(10);
  };

  const isActive = (paths) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  const items = [
    {
      key: "home",
      label: t("bottom_nav_home"),
      icon: Home,
      onClick: () => { triggerHaptic(); navigate("/"); },
      active: location.pathname === "/",
      testid: "bottomnav-home",
    },
    {
      key: "posts",
      label: t("bottom_nav_listings"),
      icon: Newspaper,
      onClick: () => { triggerHaptic(); navigate("/annonces"); },
      active: isActive(["/annonces"]),
      testid: "bottomnav-listings",
    },
    {
      key: "marketplace",
      label: t("bottom_nav_marketplace"),
      icon: Package,
      onClick: () => { triggerHaptic(); navigate("/marketplace"); },
      active: isActive(["/marketplace"]),
      testid: "bottomnav-marketplace",
    },
    {
      key: "messages",
      label: t("bottom_nav_messages"),
      icon: MessageSquare,
      onClick: () => { triggerHaptic(); navigate("/messages"); },
      active: isActive(["/messages"]),
      badge: unreadCount,
      testid: "bottomnav-messages",
    },
    {
      key: "profile",
      label: t("bottom_nav_profile"),
      icon: User,
      onClick: () => { triggerHaptic(); navigate("/profile"); },
      active: isActive(["/profile", "/dashboard"]),
      testid: "bottomnav-profile",
    },
  ];

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="flex items-stretch justify-around px-1">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.key} className="flex-1">
              <button
                data-testid={item.testid}
                onClick={item.onClick}
                aria-label={item.label}
                className={`w-full flex flex-col items-center justify-center gap-1 py-2 px-1 transition-colors ${
                  item.active ? "text-sky-600" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <span className="relative">
                  <Icon className="w-6 h-6" strokeWidth={item.active ? 2.5 : 2} />
                  {item.badge > 0 && (
                    <span
                      data-testid={`${item.testid}-badge`}
                      className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white"
                    >
                      {item.badge > 9 ? "9+" : item.badge}
                    </span>
                  )}
                </span>
                <span className={`text-[11px] leading-none ${item.active ? "font-semibold" : "font-medium"}`}>
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// ==================== APP ====================
const BottomNavSpacer = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  if (location.pathname.startsWith("/auth")) return null;
  return <div className="md:hidden h-20" aria-hidden="true" />;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <LocalBusinessSchema />
            <Toaster position="top-right" richColors />
            <AppRouter />
            <BottomNav />
            {/* Spacer to prevent bottom nav overlap (only when user connected) */}
            <BottomNavSpacer />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
